import React, { useEffect, useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import apiFetch from '../utils/apiFetch';
import { downloadAuthorizedTeamKml } from '../utils/downloadTeamKml';
import { message } from '../utils/antdMessage';
import dayjs from 'dayjs';
import { saveUserSettings, saveSetting, SETTING_KEYS } from '../utils/userSettings';
import mapStateManager from '../utils/mapStateManager';
import requestManager from '../utils/requestManager'; // 新增：请求管理器
import { applyMapboxToken } from '../utils/mapboxToken';
import DashboardAuthedWorkspace from '../components/DashboardAuthedWorkspace';
import PickPointSubmittedModal from '../components/PickPointSubmittedModal';
import useMobilePanelDrag from '../hooks/useMobilePanelDrag';
import useMemberTreeAutoExpand from '../hooks/useMemberTreeAutoExpand';
import useOnlineMemberNotifications from '../hooks/useOnlineMemberNotifications';
import useMemberPresenceTracker from '../hooks/useMemberPresenceTracker';
import useDashboardMapChromeEffects from '../hooks/useDashboardMapChromeEffects';
import useCommanderMemberNotes from '../hooks/useCommanderMemberNotes';
import useDashboardDeviceRefreshEffects from '../hooks/useDashboardDeviceRefreshEffects';
import useDashboardPerfInit from '../hooks/useDashboardPerfInit';
import useDashboardSettingsLifecycle from '../hooks/useDashboardSettingsLifecycle';
import useDashboardBasemapControls from '../hooks/useDashboardBasemapControls';
import useDashboardToolbarActions from '../hooks/useDashboardToolbarActions';
import useDashboardRealtimeTracks from '../hooks/useDashboardRealtimeTracks';
import useDashboardFetchDevices from '../hooks/useDashboardFetchDevices';
import useDashboardAppTrackAutoload from '../hooks/useDashboardAppTrackAutoload';
import useDashboardMapWsBootstrap from '../hooks/useDashboardMapWsBootstrap';
import useDashboardFieldMessages from '../hooks/useDashboardFieldMessages';
import useDashboardCommandPins from '../hooks/useDashboardCommandPins';
import { commandPinCoversLatLng } from '../utils/dashboard/commandPinHelpers';
import useDashboardTrackLineActions from '../hooks/useDashboardTrackLineActions';
import useDashboardSendLocation from '../hooks/useDashboardSendLocation';
import useDashboardRescueSearchTargetPush from '../hooks/useDashboardRescueSearchTargetPush';
import useDashboardPickPointActions from '../hooks/useDashboardPickPointActions';
import uiSettingsManager from '../utils/uiSettings';
import {
  DASHBOARD_STYLES,
  DASHBOARD_MAP_STYLES,
  checkMembersStreaming,
  buildStreamUrls, resolveMemberStreamKey, getMemberBoundStreamKey,
  updateMemberMarkersOnMap,
  displayRescueTargetOnMap as displayRescueTargetOnMapUtil,
  fetchMemberHistoryTrack,
  fetchAllMemberHistoryTracks,
  resolveDownloadTimeRange as resolveDownloadTimeRangeUtil,
  estimateViewHeight as estimateViewHeightUtil,
  resolveTrackModeTypeChange,
  flyToMemberOnMap,
  readLastCommanderLocationFromStorage,
  writeLastCommanderLocationToStorage,
  flyToLastCommanderLocation,
  readLastDeviceLocationFromStorage,
  writeLastDeviceLocationToStorage,
  buildLastDeviceLocationRecord,
  flyToLastDeviceLocationRecord,
  placeCommanderMarker,
  copyToClipboard as copyToClipboardUtil,
  getMemberExpandKey as getMemberExpandKeyUtil,
  groupMembersByOrgAndGroup,
  createSidebarDragMouseDownHandler,
  reprojectPickPointMarkers,
  filterDashboardMembers,
  getMemberPresenceStatus,
  PRESENCE_STATUS,
  fetchPublicAnnouncementState,
  closePublicAnnouncement,
  downloadMemberTrackAsKml,
  fetchOpenElevation,
  dashboardDebug,
  buildIdentityUpdateNotification,
  readCachedSidebarChrome
} from '../utils/dashboard';
import { readPersistedTrackModeState, trackModeLocalKey } from '../utils/dashboard/dashboardSettingsHelpers';

import { hasPermission, hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';
import {
  buildDashboardCommandFeatures,
  buildMemberTreeBlockProps,
  buildDashboardLayoutProps
} from '../utils/dashboard/dashboardWorkspaceProps';
import { setMissionVoicePttAllow, collectVoicePttTargets, cleanupMissionVoiceJunk } from '../utils/dashboard/missionVoicePtt';
import { setMissionRecordTracks, fetchRecentHistoryTrackPoints } from '../utils/dashboard/missionTrackRecord';
import {
  voiceMemberKeys,
  shouldAutoPlayVoiceForMissionContext,
  summarizeUnreadForMemberIds
} from '../utils/dashboard/voiceUnreadHelpers';
import {
  scheduleMissionsRosterSync,
  endMissionRosterOnServer,
  endMissionsRosterOnServer
} from '../utils/dashboard/missionRosterSync';
import { stopVoicePttPlayback } from '../utils/dashboard/voicePttPlayback';
import {
  persistMissionState,
  loadCommandMissionState,
  pruneMissionGroups
} from '../utils/dashboard/missionPersist';
import { liveVoiceErrorMessage, LIVE_VOICE_ERRORS } from '../utils/dashboard/liveVoiceErrorConfig';
import {missionIdMatchesMember, liveMissionMemberKeys, sanitizeMissionMemberIds,
  getCanonicalMemberId} from '../utils/dashboard/deviceIdentity';
import {
  createEmptyMission,
  getActiveMission,
  countUniqueLiveAcrossMissions,
  liveCountForMission,
  memberInOtherMissions,
  replaceMission,
  removeMission,
  migrateLegacyMissions,
  membersInRecordTrackScope
} from '../utils/dashboard/missionsModel';
import { injectDashboardStyles } from '../utils/dashboard/injectDashboardStyles';
import {
  renderHistoryTracksOnMap,
  safelyDestroyMap,
  fitMapToHistoryTrack,
  trackHasUsableCoordinates
} from '../utils/dashboard/historyTrackLayers';
import {
  createEarthSpinController,
  installDashboardRescueFlyGlobals,
  pauseEarthSpinFramesSilent,
  exitEarthToCommandView,
  markDashboardEarthDismissed,
  isDashboardEarthDismissed,
  schedulePostEarthDiveCleanup
} from '../utils/dashboard/mapInitHelpers';
import {
  DEFAULT_MAP_PROVIDER_SETTINGS,
  MAP_PROVIDER_CATALOG,
  normalizeMapProviderSettings,
  applyBasemap,
  buildRasterMapStyle,
  resolveStartupBasemapId,
  resolveStartupStyleMode,
  readLastOverlayChoice,
  ensureMandatoryOverlays,
  syncOverlaysForStyleMode,
  isProviderEnabled,
  applyCustomTileDatums
} from '../utils/mapProviderSettings';
import { setActiveBasemapIdForCoord, toMapLngLat, fromMapLngLat } from '../utils/dashboard/basemapCoord';
import { isBogusGpsCoordinate, isPlausibleCommandCoordinate, purgeBogusMapLocalState } from '../utils/bogusGps';
import { rehydrateMapLayerOverlays } from '../utils/mapLayerOverlay';
import { whenMapStyleSettled } from '../utils/dashboard/earthDeepSpace';
import { createMemberTreeExpandActions } from '../utils/dashboard/memberTreeExpandActions';
import { createDashboardRescueActions } from '../utils/dashboard/dashboardRescueActions';


// 设置 Mapbox Token（运行时配置 / 环境变量 / localStorage）
applyMapboxToken(mapboxgl);

// ONLINE_TIMEOUT / DASHBOARD_STYLES 已拆至 utils/dashboard/constants.js

// 主控制面板（口令通过后挂载）
function DashboardAuthenticated() {
  // 优化CSS样式注入 - 与门禁壳共用同一标记，避免重复注入
  React.useEffect(() => {
    injectDashboardStyles(DASHBOARD_STYLES);
    window.dashboardLoadTime = Date.now();
    window.dashboardSettingsHydrated = false;
  }, []);

  // 添加全局飞行函数，供列表点击调用
  useEffect(() => {
    return installDashboardRescueFlyGlobals(() => mapRef.current);
  }, []);

  // 队员位置和设备信息状态
  const [members, setMembers] = useState([]);
  const membersRef = useRef([]); // 供地图点击/自转回调读取最新队员，避免 useEffect([]) 闭包过期
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]); // 用于存储地图上的标记物，方便管理
  const pickPointMarkersRef = useRef(new Map());
  const [pickPoints, setPickPoints] = useState([]);
  
  // 选点确认弹窗状态
  const [pickPointSubmittedModal, setPickPointSubmittedModal] = useState({
    visible: false,
    point: null,
    confirming: false
  });

  useEffect(() => {
    membersRef.current = members;
  }, [members]);
  
  // 救援数据状态
  const [rescueTargets, setRescueTargets] = useState([]); // 救援目标
  const [rescueTracks, setRescueTracks] = useState([]); // 救援轨迹
  const rescueTargetMarkersRef = useRef([]); // 救援目标标记引用
  const commandPinsMapBridgeRef = useRef(null); // 与 useDashboardCommandPins 的 pins Map 同步，供救援上图去重
  /** 种点发送时缓存显示坐标，避免 WS 回推时 WGS↔显示换算导致针尖再跳一次 */
  const rescueDisplayLngLatCacheRef = useRef(new Map());
  const rescueTrackLinesRef = useRef([]); // 救援轨迹线条引用
  const openComposeAtWgsRef = useRef(null);
  const [openIndex, setOpenIndex] = useState(null); // 当前展开的队员索引，null表示全部收起
  // 组织和小组折叠状态
  const [openOrg, setOpenOrg] = useState({}); // 记录每个组织的展开状态
  const [openGroup, setOpenGroup] = useState({}); // 记录每个小组的展开状态
  const [openMember, setOpenMember] = useState({}); // 记录每个队员的展开状态
  const memberTreeHydratedRef = useRef(false); // 队员树是否已完成首次自动展开

  // 队员折叠 key：用稳定 ID，避免 last_seen 变化导致状态丢失/被重置
  const getMemberExpandKey = getMemberExpandKeyUtil;
  // 手机选点回调已拆至 hooks/useDashboardPickPointActions.js（在 currentBasemapIdRef 声明后调用）


  const [collapsed, setCollapsed] = useState(() => readCachedSidebarChrome().collapsed); // 队员信息区域收缩状态
  // 筛选和显示控制
  const [startTime, setStartTime] = useState(''); // 开始时间筛选
  const [endTime, setEndTime] = useState(''); // 结束时间筛选
  // 使用useRef存储时间变量，避免暂时性死区问题
  const startTimeRef = useRef('');
  const endTimeRef = useRef('');
  
  // 同步状态变量和ref变量
  useEffect(() => {
    startTimeRef.current = startTime;
  }, [startTime]);
  
  useEffect(() => {
    endTimeRef.current = endTime;
  }, [endTime]);
  
  const [hideInvalid, setHideInvalid] = useState(false); // 隐藏无效会员
  const [showOnlineOnly, setShowOnlineOnly] = useState(false); // 默认保留离线（显示设置天数窗）；仅勾选时过滤在线
  const [memberSearchQuery, setMemberSearchQuery] = useState(''); // 队员列表搜索（姓名/组织/小组）
  /** 任务中：仅显示筛选勾选的队员（无独立任务模型时用勾选集作最小可行） */
  const [missionMode, setMissionMode] = useState(false);
  const [missionMemberIds, setMissionMemberIds] = useState([]); // device_unique_id || name (active mission)
  const missionMemberIdsRef = useRef(missionMemberIds);
  missionMemberIdsRef.current = missionMemberIds;
  /** Multi-mission list; activeMissionId drives kanban/map filter */
  const [missions, setMissions] = useState([]);
  const [activeMissionId, setActiveMissionId] = useState(null);
  /** 侧栏标签：all=组织大树；mission=任务看板（待编组+临时分组） */
  const [memberPanelTab, setMemberPanelTab] = useState('all'); // 'all' | 'mission' | 'playback' — 项 11：标签只过滤视图，不改看板权威数据
  /** 全部页勾选，待加入任务 */
  const [missionPickIds, setMissionPickIds] = useState([]);
  /** 任务中临时分组（仅指挥侧）：{ id, name, collapsed, memberIds, color }[] */
  const [missionGroups, setMissionGroups] = useState([]);
  /** 地图上隐藏坐标的队员 id（device_unique_id || name），侧栏仍显示 */
  const [hiddenMapMemberIds, setHiddenMapMemberIds] = useState(() => new Set());
  const hiddenMapMemberIdsRef = useRef(hiddenMapMemberIds);
  useEffect(() => {
    hiddenMapMemberIdsRef.current = hiddenMapMemberIds;
  }, [hiddenMapMemberIds]);
  const [trackMode, setTrackMode] = useState(() => {
    // 按当前登录帐号读缓存，避免换指挥员后沿用上一帐号的天数/模式
    return readPersistedTrackModeState().trackMode || 'recent';
  }); // 轨迹模式：recent/recent_hours/recent_days/all
  const [trackMinutes, setTrackMinutes] = useState(() => readPersistedTrackModeState().trackMinutes);
  const [trackHours, setTrackHours] = useState(() => readPersistedTrackModeState().trackHours);
  const [trackDays, setTrackDays] = useState(() => {
    const result = readPersistedTrackModeState().trackDays;
    dashboardDebug('trackDays初始化:', { result });
    return result;
  }); // 最近N天
  const [playbackRange, setPlaybackRange] = useState({ start: null, end: null }); // 轨迹回放时间范围
  const [isPlaying, setIsPlaying] = useState(false); // 是否正在回放
  const [playbackIndex, setPlaybackIndex] = useState(0); // 当前回放索引
  const [trackColors, setTrackColors] = useState(() => JSON.parse(localStorage.getItem('trackColors')||'{}'));
  const [expandedGroups, setExpandedGroups] = useState({}); // 记录每个小组/组织展开状态
  const [showDownload, setShowDownload] = useState(true); // 权限判断，实际应从用户权限获取
  const [showTracks, setShowTracks] = useState({}); // 记录哪些队员历史轨迹线正在显示
  const [trackData, setTrackData] = useState({}); // 存储每个队员的轨迹点
  const [allTracksVisible, setAllTracksVisible] = useState(false); // 是否一键显示所有轨迹
  const [forceRefresh, setForceRefresh] = useState(false); // 强制刷新轨迹数据
  /** 管理后台：实时绘制持续离线宽限（分钟） */
  const [realtimeTrackOfflineGraceMinutes, setRealtimeTrackOfflineGraceMinutes] = useState(30);
  /** 管理后台：暂时离线（橙）/ 正式离线（灰）分钟数；指挥页约 5 秒按静默重算 */
  const [memberTempOfflineMinutes, setMemberTempOfflineMinutes] = useState(5);
  const [memberOfflineMinutes, setMemberOfflineMinutes] = useState(15);
  /** 地图水滴图案是否按速度自动切换 */
  const [memberMarkerSpeedAuto, setMemberMarkerSpeedAuto] = useState(true);
  const memberTempOfflineTimeoutMs = useMemo(
    () => Math.max(1, Number(memberTempOfflineMinutes) || 5) * 60 * 1000,
    [memberTempOfflineMinutes]
  );
  const memberOfflineTimeoutMs = useMemo(() => {
    const tempMin = Math.max(1, Number(memberTempOfflineMinutes) || 5);
    const offlineMin = Math.max(tempMin + 1, Number(memberOfflineMinutes) || 15);
    return offlineMin * 60 * 1000;
  }, [memberTempOfflineMinutes, memberOfflineMinutes]);
  const {
    presenceById,
    notifyDevicesFetched
  } = useMemberPresenceTracker({
    members,
    tempOfflineTimeoutMs: memberTempOfflineTimeoutMs,
    offlineTimeoutMs: memberOfflineTimeoutMs
  });
  const presenceByIdRef = useRef(presenceById);
  useEffect(() => { presenceByIdRef.current = presenceById; }, [presenceById]);
  // 队员状态侧栏宽度：首屏同步读缓存，避免刷新时 480→存档宽度 的伸缩动画
  const [sidebarWidth, setSidebarWidth] = useState(() => readCachedSidebarChrome().sidebarWidth);
  // 首屏禁止宽度 transition；设置 hydrate / 首帧稳定后再允许（拖拽折叠仍有动画）
  const [sidebarMotionEnabled, setSidebarMotionEnabled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSidebarMotionEnabled(true), 500);
    return () => clearTimeout(t);
  }, []);
  const sidebarRef = useRef();
  // 高亮队员ID集合，支持多个高亮
  const [highlightedMemberIds, setHighlightedMemberIds] = useState(new Set());
  // 队员树展开/收起操作（已拆至 utils/dashboard/memberTreeExpandActions.js）
  const {
    setOrgGroupsExpanded,
    expandOnlineInOrg,
    setGroupMembersExpanded,
    expandOnlineInGroup,
    areAllGroupsExpanded,
    areAllMembersExpanded
  } = createMemberTreeExpandActions({
    openGroup,
    openMember,
    highlightedMemberIds,
    setOpenGroup,
    setOpenMember,
    setHighlightedMemberIds
  });
  // 当前激活的成员ID
  const [activeMemberId, setActiveMemberId] = useState(null);
  // 新增：地图状态信息（缩放级别、离地高度、中心海拔）
  const [mapStatus, setMapStatus] = useState({
    zoom: 10,
    center: [119.13643, 28.07741],
    height: null, // 离地高度（米）；地球自转时为 null
    elevation: null, // 中心海拔（米）
    pointerElevation: null, // 鼠标指针处海拔（米）
    heightStatus: 'idle', // idle | loading | ok | paused
    elevationStatus: 'idle', // idle | loading | ok | unavailable | paused
    pointerElevationStatus: 'idle' // idle | loading | ok | unavailable | paused
  });
  const refreshMapStatusRef = useRef(() => {});
  
  // 登录状态检查
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return !!localStorage.getItem('token');
  });
  
  // 已通过外层口令门禁；本组件仅在授权后挂载
  const authed = true;
  // 公告内容状态
  const [announcement, setAnnouncement] = useState(null); // {id, content, start_time, end_time}
  const [showAnnouncement, setShowAnnouncement] = useState(true); // 控制公告显示/隐藏

  // 轨迹回放动画定时器引用
  const playbackTimerRef = useRef(null); // 用于保存定时器ID，便于停止动画
  // 新增：用户设置加载状态
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [highlightedMemberName, setHighlightedMemberName] = useState(null); // 当前高亮的队员名字
  const [trackModeApplied, setTrackModeApplied] = useState(false); // 轨迹模式时间筛选是否已应用
  // 新增：队员状态刷新和上线公告相关状态
  const [lastOnlineMembers, setLastOnlineMembers] = useState(new Set()); // 上次刷新时的在线队员集合
  const [onlineNotifications, setOnlineNotifications] = useState([]); // 上线公告列表
  const onlineBaselineReadyRef = useRef(false); // 是否已完成首次在线基线（避免刷新误报上线）
  const [refreshInterval, setRefreshInterval] = useState(60000); // 刷新间隔，默认1分钟，将从用户设置中加载
  // 新增：设备性能和网络状态相关状态
  const [networkQuality, setNetworkQuality] = useState('good'); // 网络质量：good/fair/poor
  const [devicePerformance, setDevicePerformance] = useState('medium'); // 设备性能：high/medium/low
  const [lastRefreshTime, setLastRefreshTime] = useState(null); // 最后刷新时间
  const [refreshStatus, setRefreshStatus] = useState('idle'); // 刷新状态：idle/loading/error
  const [showMemberNames, setShowMemberNames] = useState(true); // 新增：队员名字显示开关
  
  // 优化：使用useMemo缓存计算值，减少重复计算
  const uniqueMembers = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (let i = members.length - 1; i >= 0; i--) {
      const m = members[i];
      if (!seen.has(m.name)) {
        seen.add(m.name);
        result.unshift(m);
      }
    }
    return result;
  }, [members]);
  
  // 优化：使用useMemo缓存过滤后的队员列表
    const listMembers = useMemo(() => {
    if (!members.length) return [];
    return filterDashboardMembers(members, {
      startTime,
      endTime,
      hideInvalid,
      showOnlineOnly,
      searchQuery: memberSearchQuery,
      missionMode: false,
      missionMemberIds: null,
      onlineTimeout: memberTempOfflineTimeoutMs,
      offlineTimeout: memberOfflineTimeoutMs,
      presenceById
    });
  }, [members, startTime, endTime, hideInvalid, showOnlineOnly, memberSearchQuery, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs, presenceById]);

  /** 地图可见集：任务中时仅勾选集（仍叠加隐藏无效/只显示在线） */
  const filteredMembers = useMemo(() => {
    if (!members.length) return [];
    const filtered = filterDashboardMembers(members, {
      startTime,
      endTime,
      hideInvalid,
      showOnlineOnly,
      searchQuery: memberSearchQuery,
      missionMode,
      missionMemberIds,
      onlineTimeout: memberTempOfflineTimeoutMs,
      offlineTimeout: memberOfflineTimeoutMs,
      presenceById
    });
    if (showOnlineOnly || memberSearchQuery || missionMode || filtered.length !== members.length) {
      dashboardDebug('队员过滤结果:', {
        total: members.length,
        filtered: filtered.length,
        showOnlineOnly,
        searchQuery: memberSearchQuery,
        missionMode,
        missionCount: countUniqueLiveAcrossMissions(missions, members),
        startTime,
        endTime,
        hideInvalid
      });
    }
    return filtered;
  }, [members, startTime, endTime, hideInvalid, showOnlineOnly, memberSearchQuery, missionMode, missionMemberIds, missions, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs, presenceById]);

  /** 面板标题/快捷条用：不受「只显示在线」影响的人数与在线数 */

  const filteredMembersRef = useRef(filteredMembers);
  filteredMembersRef.current = filteredMembers;

  const memberPanelTotalCount = useMemo(() => {
    if (!members.length) return 0;
    return filterDashboardMembers(members, {
      startTime,
      endTime,
      hideInvalid,
      showOnlineOnly: false,
      searchQuery: memberSearchQuery,
      onlineTimeout: memberTempOfflineTimeoutMs,
      offlineTimeout: memberOfflineTimeoutMs,
      presenceById
    }).length;
  }, [members, startTime, endTime, hideInvalid, memberSearchQuery, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs, presenceById]);

  const memberPanelOnlineCount = useMemo(() => {
    if (!members.length) return 0;
    const base = filterDashboardMembers(members, {
      startTime,
      endTime,
      hideInvalid,
      showOnlineOnly: false,
      searchQuery: memberSearchQuery,
      onlineTimeout: memberTempOfflineTimeoutMs,
      offlineTimeout: memberOfflineTimeoutMs,
      presenceById
    });
    const now = Date.now();
    let n = 0;
    base.forEach((member) => {
      const id = member.device_unique_id || member.name;
      const status = (presenceById && id && presenceById[id])
        || getMemberPresenceStatus(member, now, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs);
      if (status === PRESENCE_STATUS.ONLINE || status === PRESENCE_STATUS.TEMP_OFFLINE) n += 1;
    });
    return n;
  }, [members, startTime, endTime, hideInvalid, memberSearchQuery, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs, presenceById]);


  // 流媒体直播相关状态
  const [streamingMembers, setStreamingMembers] = useState(new Set()); // 正在直播的队员
  const [showLiveStream, setShowLiveStream] = useState(false); // 是否显示直播弹窗
  const [currentStreamMember, setCurrentStreamMember] = useState(null); // 当前直播的队员
  // 直播弹窗内推流/观看地址：默认折叠，展开需指挥页同一访问密码

  // 队员轨迹时间选择弹窗：获取 / 下载（默认当天）
  const [trackDownloadModal, setTrackDownloadModal] = useState({
    visible: false,
    mode: 'download', // fetch | download
    member: null,
    preset: 'today', // today | last7 | last30 | all | custom
    range: [dayjs().startOf('day'), dayjs().endOf('day')],
    loading: false
  });

  // 新增：左侧弹出框控制状态
  const [showLeftPanel, setShowLeftPanel] = useState(false); // 控制左侧面板显示/隐藏
  const [leftPanelVisible, setLeftPanelVisible] = useState(false); // 控制左侧面板动画显示
  
  // 实时轨迹状态改由 useDashboardRealtimeTracks 持有

  // 新增：地球动画相关状态
  const [isEarthAnimationActive, setIsEarthAnimationActive] = useState(false); // 地球自转动画是否激活
  const [mapLoadingState, setMapLoadingState] = useState('loading'); // 地图加载状态：loading, loaded, error
  const earthAnimationRef = useRef(null); // 地球动画定时器引用
  const isAnimationActiveRef = useRef(false); // 动画状态的ref引用，用于异步函数
  const shouldUseEarthOverviewRef = useRef(false); // 当前是否应使用地球全览初始化流程
  const mapStyleReadyRef = useRef(false); // 地图 style.load 是否已完成（用于 Strict Mode 卸载判断）
  const startEarthAnimationRef = useRef(() => {});
  const flyToLatestMemberRef = useRef(() => {});
  const flyToMemberRef = useRef(() => {});
  const smartFlyToDevicesRef = useRef(() => {});
  // 帐号用「定位工具」留下的指挥员坐标（地球模式下点空白飞到此记录）
  const lastCommanderLocationRef = useRef(
    readLastCommanderLocationFromStorage(localStorage.getItem('username') || '')
  );
  // 启动时清掉 Chrome 等浏览器里残留的 MaxMind 伪定位存档
  useEffect(() => {
    purgeBogusMapLocalState();
    const last = lastCommanderLocationRef.current;
    if (!last || !isPlausibleCommandCoordinate(last.lat, last.lng)) {
      lastCommanderLocationRef.current = null;
    }
  }, []);

  // 画板工具激活标记（画板自管地图交互；空白点击不再触发队员飞行）
  const drawingToolsActiveRef = useRef(false);
  // 底图样式 id 的 ref：地球动画启动早于 basemap hook 声明，避免闭包/TDZ 脆点
  const currentMapStyleIdRef = useRef('streets');
  // 多底图：优先读取管理后台（公开 UI 设置）的缓存，随后异步刷新。
  const [mapProviderSettings, setMapProviderSettings] = useState(() =>
    normalizeMapProviderSettings(
      (uiSettingsManager.getSettingsSync() || {}).mapProviderSettings || DEFAULT_MAP_PROVIDER_SETTINGS
    )
  );
  const mapProviderSettingsRef = useRef(mapProviderSettings);
  // 刷新后始终用管理后台「默认底图」，不恢复浏览器里上次选的谷歌等外网源
  const [currentBasemapId, setCurrentBasemapId] = useState(() => {
    const settings = normalizeMapProviderSettings(
      (uiSettingsManager.getSettingsSync() || {}).mapProviderSettings || DEFAULT_MAP_PROVIDER_SETTINGS
    );
    return resolveStartupBasemapId(settings);
  });
  const [activeOverlayIds, setActiveOverlayIds] = useState(() => {
    const settings = normalizeMapProviderSettings(
      (uiSettingsManager.getSettingsSync() || {}).mapProviderSettings || DEFAULT_MAP_PROVIDER_SETTINGS
    );
    const basemapId = resolveStartupBasemapId(settings);
    const mode = resolveStartupStyleMode(settings, basemapId);
    return syncOverlaysForStyleMode(basemapId, mode, readLastOverlayChoice(), settings);
  });
  const [showBasemapPanel, setShowBasemapPanel] = useState(false);
  const currentBasemapIdRef = useRef(currentBasemapId);
  const activeOverlayIdsRef = useRef(activeOverlayIds);
  // 本会话内用户手动切过底图后，异步拉取的后台配置不再强行改回默认
  const basemapUserPickedRef = useRef(false);
  // 底图切换完成后重绘纠偏图层（定义在 updateMarkers 之后写入）
  const refreshMapOverlaysAfterBasemapRef = useRef(null);
  const loadFieldMessagePinsRef = useRef(null);
  const reprojectFieldMessagePinsOnMapRef = useRef(null);
  const loadCommandPinsRef = useRef(null);
  const reprojectCommandPinsOnMapRef = useRef(null);
  // 手机选点：显示/删除/加载/提交弹窗/飞行/确认（已拆至 hooks/useDashboardPickPointActions.js）
  const {
    handleDeletePickPoint,
    displayPickPointOnMap,
    removePickPointFromMap,
    loadPickPoints,
    handlePickPointSubmitted,
    flyToPickPoint,
    confirmPickPoint
  } = useDashboardPickPointActions({
    mapRef,
    pickPointMarkersRef,
    currentBasemapIdRef,
    setPickPoints,
    setPickPointSubmittedModal
  });

  useEffect(() => {
    mapProviderSettingsRef.current = mapProviderSettings;
    // 自定义影像坐标系（后台可声明；全部默认时与改造前一致）
    applyCustomTileDatums(mapProviderSettings);
  }, [mapProviderSettings]);
  useEffect(() => {
    currentBasemapIdRef.current = currentBasemapId;
    setActiveBasemapIdForCoord(currentBasemapId, mapRef.current);
  }, [currentBasemapId]);
  useEffect(() => { activeOverlayIdsRef.current = activeOverlayIds; }, [activeOverlayIds]);
  useEffect(() => {
    let cancelled = false;
    uiSettingsManager.getAllSettings()
      .then((settings) => {
        if (cancelled) return;
        const next = normalizeMapProviderSettings(settings?.mapProviderSettings);
        applyCustomTileDatums(next);
        mapProviderSettingsRef.current = next;
        setMapProviderSettings(next);
        // 含天地图 Key 的后台配置异步到达：同底图不重建样式时也要补上国界线
        if (mapRef.current) ensureMandatoryOverlays(mapRef.current, next);
        const selectedBasemapId = resolveStartupBasemapId(next);
        const startupMode = resolveStartupStyleMode(next, selectedBasemapId);
        setActiveOverlayIds((ids) => {
          const kept = ids.filter((id) => isProviderEnabled(next, id));
          return syncOverlaysForStyleMode(selectedBasemapId, startupMode, kept, next);
        });
        // 刷新进入：应用后台默认底图；若用户已在本页手动切换则尊重其选择
        if (basemapUserPickedRef.current) return;
        setActiveBasemapIdForCoord(selectedBasemapId, mapRef.current);
        if (mapRef.current && selectedBasemapId !== currentBasemapIdRef.current) {
          applyBasemap(mapRef.current, selectedBasemapId, activeOverlayIdsRef.current, next, {
            globe: Boolean(shouldUseEarthOverviewRef.current || isAnimationActiveRef.current),
            onSettled: () => refreshMapOverlaysAfterBasemapRef.current?.()
          });
        }
        currentBasemapIdRef.current = selectedBasemapId;
        setCurrentBasemapId(selectedBasemapId);
      })
      .catch((error) => console.warn('加载底图配置失败，使用默认设置:', error));
    return () => { cancelled = true; };
  }, []);
  
  const createDashboardEarthSpin = () => createEarthSpinController({
    getMap: () => mapRef.current,
    animationRef: earthAnimationRef,
    isActiveRef: isAnimationActiveRef,
    onActiveChange: (active) => {
      setIsEarthAnimationActive(active);
      if (active && !isDashboardEarthDismissed()) {
        shouldUseEarthOverviewRef.current = true;
      }
    }
  });

  // 启动地球自转动画（逐帧微调经度，避免 easeTo 叠加导致抖动）
  const startEarthAnimation = () => {
    console.log('尝试启动地球自转动画, mapRef.current:', !!mapRef.current, 'isAnimationActiveRef:', isAnimationActiveRef.current);
    const started = createDashboardEarthSpin().start();
    if (!started) return;
    // 勿用地球低缩放覆盖指挥台上次视野（否则 F5 恢复成全球图）
    console.log('地球动画已启动');
  };
  startEarthAnimationRef.current = startEarthAnimation;

  // 停止地球自转动画
  // clearOverviewFlag=false：仅暂停动画（如组件卸载），保留“地球全景中”标记以便返回后继续转
  const stopEarthAnimation = (options = {}) => {
    const { clearOverviewFlag = true } = options;
    createDashboardEarthSpin().stop({ clearOverviewFlag });
    if (clearOverviewFlag) {
      shouldUseEarthOverviewRef.current = false;
    }
    // 退出地球后若正在 flyTo 俯冲，等落地再刷海拔，避免飞入中途卡顿
    if (clearOverviewFlag) {
      setTimeout(() => {
        const map = mapRef.current;
        const run = () => refreshMapStatusRef.current?.({ force: true });
        try {
          if (map?.isMoving?.()) {
            map.once('moveend', run);
            return;
          }
        } catch (_) {
          /* ignore */
        }
        run();
      }, 120);
    }
  };
  // 地球模式下点空白：优先飞「上次队员设备定位」→ 指挥员定位 → 龙泉默认
  const flyToLatestMember = () => {
    // 尽早标记离开地球，避免异步 applyBasemap 用 zoom≈1 把相机拽回全景
    markDashboardEarthDismissed();
    shouldUseEarthOverviewRef.current = false;
    purgeBogusMapLocalState();

    const username = localStorage.getItem('username') || '';
    const deviceRecord = readLastDeviceLocationFromStorage();
    let commanderRecord = lastCommanderLocationRef.current
      || readLastCommanderLocationFromStorage(username);
    if (commanderRecord && !isPlausibleCommandCoordinate(commanderRecord.lat, commanderRecord.lng)) {
      lastCommanderLocationRef.current = null;
      commanderRecord = null;
    }

    // 先静默停帧，再走完整 stop（更新 UI / 清全览标记），避免自转 jumpTo 打断 flyTo
    pauseEarthSpinFramesSilent({
      animationRef: earthAnimationRef,
      isActiveRef: isAnimationActiveRef
    });

    // 1) 同一浏览器曾定位过队员设备 → 刷新后默认飞回该设备
    if (deviceRecord) {
      console.log('飞行到上次设备定位记录:', deviceRecord);
      stopEarthAnimation();
      const ok = flyToLastDeviceLocationRecord(mapRef.current, deviceRecord, {
        heightMeters: 600,
        duration: 3200,
        basemapId: currentBasemapIdRef.current
      });
      if (ok) {
        schedulePostEarthDiveCleanup(mapRef.current, {
          onComplete: () => {
            refreshMapStatusRef.current?.({ force: true });
            // 退出地球模式后重新渲染所有 HTML 标记
            try {
              updateMarkers(membersRef.current || members);
            } catch (e) {
              console.warn('退出地球后刷新队员标记失败:', e);
            }
            try {
              loadRescueData?.();
            } catch (e) {
              console.warn('退出地球后刷新救援标记失败:', e);
            }
            try {
              loadFieldMessagePinsRef.current?.();
            } catch (e) {
              console.warn('退出地球后刷新消息打点失败:', e);
            }
            try {
              loadCommandPinsRef.current?.();
            } catch (e) {
              console.warn('退出地球后刷新指令位置点失败:', e);
            }
            try {
              loadPickPoints?.();
            } catch (e) {
              console.warn('退出地球后刷新接送点失败:', e);
            }
          }
        });
        const label = deviceRecord.name || '设备';
        setTimeout(() => {
          if (mapRef.current) {
            message.success(`已定位到上次设备位置：${label}`);
          }
        }, 3500);
        return;
      }
    }

    // 2) 指挥员定位工具留下的坐标
    if (commanderRecord) {
      lastCommanderLocationRef.current = commanderRecord;
      console.log('飞行到指挥员上次定位记录:', commanderRecord);
      stopEarthAnimation();
      const ok = flyToLastCommanderLocation(mapRef.current, commanderRecord, {
        heightMeters: 600,
        duration: 3200,
        fromEarthOverview: true,
        basemapId: currentBasemapIdRef.current,
        onEarthDiveScheduled: (map) => {
          schedulePostEarthDiveCleanup(map, {
            onComplete: () => {
              refreshMapStatusRef.current?.({ force: true });
              // 退出地球模式后重新渲染所有 HTML 标记
              try {
                updateMarkers(membersRef.current || members);
              } catch (e) {
                console.warn('退出地球后刷新队员标记失败:', e);
              }
              try {
                loadRescueData?.();
              } catch (e) {
                console.warn('退出地球后刷新救援标记失败:', e);
              }
              try {
                loadFieldMessagePinsRef.current?.();
              } catch (e) {
                console.warn('退出地球后刷新消息打点失败:', e);
              }
              try {
                loadCommandPinsRef.current?.();
              } catch (e) {
                console.warn('退出地球后刷新指令位置点失败:', e);
              }
              try {
                loadPickPoints?.();
              } catch (e) {
                console.warn('退出地球后刷新接送点失败:', e);
              }
            }
          });
        }
      });
      if (!ok) {
        lastCommanderLocationRef.current = null;
        exitEarthToCommandView({
          getMap: () => mapRef.current,
          stopEarthAnimation,
          preferSavedState: false,
          defaultState: mapStateManager.getDefaultMapState()
        });
        message.warning('指挥员上次定位不可用，已进入龙泉默认指挥视野');
        return;
      }
      const timeText = commanderRecord.locatedAtRaw
        ? new Date(commanderRecord.locatedAtRaw).toLocaleString()
        : (commanderRecord.locatedAt ? new Date(commanderRecord.locatedAt).toLocaleString() : '未知时间');
      setTimeout(() => {
        if (mapRef.current) {
          message.success(`已定位到指挥员上次位置（${timeText}）`);
        }
      }, 3500);
      return;
    }

    // 3) 都没有：落龙泉
    exitEarthToCommandView({
      getMap: () => mapRef.current,
      stopEarthAnimation,
      preferSavedState: false,
      defaultState: mapStateManager.getDefaultMapState()
    });
    message.info('已进入龙泉指挥视野。可点右下角定位，或点左侧队员名字。');
  };
  flyToLatestMemberRef.current = flyToLatestMember;

  // 空白点击飞行：仅地球全览/自转阶段可用；进入常规地图后禁用。
  // 常规地图下通过队员标记、队员树、上线提示等显式操作飞行。
  const smartFlyToDevices = () => {
    console.log('已禁用自动智能定位（非空白点击路径）');
  };
  smartFlyToDevicesRef.current = smartFlyToDevices;

  // 创建水滴状救援推送图标 - 最简化版本，确保精确定位
  // 新增：左侧面板切换函数
  const toggleLeftPanel = () => {
    if (showLeftPanel) {
      setLeftPanelVisible(false);
      setTimeout(() => {
        setShowLeftPanel(false);
      }, 300); // 等待动画完成
    } else {
      setShowLeftPanel(true);
      setTimeout(() => {
        setLeftPanelVisible(true);
      }, 50); // 短暂延迟确保DOM更新
    }
  };

  // 流媒体直播相关函数
  const checkAllStreams = async () => {
    const newStreamingMembers = await checkMembersStreaming(members);
    setStreamingMembers(newStreamingMembers);
  };

  const openLiveStream = (member) => {
    const key = getMemberBoundStreamKey(member);
    if (!key) {
      console.warn('[live]', LIVE_VOICE_ERRORS.STREAM_KEY_MISSING, member?.name);
      try { message.warning(liveVoiceErrorMessage('STREAM_KEY_MISSING')); } catch (_) { /* ignore */ }
      return;
    }
    setCurrentStreamMember(member);
    setShowLiveStream(true);
    window.currentStreamUrls = {
      ...buildStreamUrls(key),
      member: member.name
    };
  };

  const closeLiveStream = () => {
    setShowLiveStream(false);
    setCurrentStreamMember(null);
  };

  // 通用复制到剪贴板函数
  const copyToClipboard = (text) => copyToClipboardUtil(text);

  const handleDragMouseDown = createSidebarDragMouseDownHandler({
    getSidebarWidth: () => sidebarWidth,
    getSidebarEl: () => sidebarRef.current,
    setSidebarWidth,
    setCollapsed,
    persistCollapsed: (v) => saveSetting(SETTING_KEYS.COLLAPSED, v),
    persistWidth: (v) => saveSetting(SETTING_KEYS.SIDEBAR_WIDTH, v),
    onAfterResize: () => {
      if (mapRef.current?.resize) {
        setTimeout(() => mapRef.current.resize(), 10);
      }
    }
  });

  // 点击队员图标：飞行定位 + 打开指挥对话弹窗
  const [memberChatTarget, setMemberChatTarget] = useState(null);
  const { notesByKey, saveNote: saveMemberNote } = useCommanderMemberNotes(true);
  const [voicePulseIds, setVoicePulseIds] = useState(() => new Set());
  const [missionChatMember, setMissionChatMember] = useState(null);
  const openChatKeysRef = useRef(new Set());
  const voicePulseTimersRef = useRef(new Map());
  /** key -> entry；跨任务未读持久，打开对话或播完才清 */
  const voiceUnreadMapRef = useRef(new Map());
  const memberPanelTabRef = useRef(memberPanelTab);
  memberPanelTabRef.current = memberPanelTab;
  const activeMissionIdRef = useRef(activeMissionId);
  activeMissionIdRef.current = activeMissionId;
  const handleVoicePttReceivedRef = useRef(null);
  const handleVoicePttAutoPlayResultRef = useRef(null);
  const tipUnreadVoicesForMemberIdsRef = useRef(null);
  const openMemberChatRef = useRef(null);
  const openMemberChat = useCallback((payloadOrMember) => {
    if (!payloadOrMember) return;
    if (!hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND)) {
      return;
    }
    const list = membersRef.current || [];
    const found = list.find(
      (m) =>
        (payloadOrMember.device_unique_id && m.device_unique_id === payloadOrMember.device_unique_id)
        || (payloadOrMember.name && m.name === payloadOrMember.name)
    );
    const target = found || payloadOrMember;
    // Locus / 单兵均支持文本与语音指挥对话
    setMemberChatTarget(target);
    if (found || (target.last_latitude && target.last_longitude)) {
      flyToMemberRef.current?.(target);
    }
  }, []);
  openMemberChatRef.current = openMemberChat;

  useEffect(() => {
    const m = missionChatMember || memberChatTarget;
    const keys = new Set();
    if (m?.device_unique_id) keys.add(String(m.device_unique_id));
    if (m?.name) keys.add(String(m.name));
    openChatKeysRef.current = keys;
  }, [missionChatMember, memberChatTarget]);

  const getMemberMapId = (member) => member?.device_unique_id || member?.name || '';

  const toggleMemberMapHidden = useCallback((member) => {
    const id = getMemberMapId(member);
    if (!id) return;
    setHiddenMapMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // 定义一个更新地图标记的函数，以便复用
  const updateMarkers = (currentMembers) => {
    const hidden = hiddenMapMemberIdsRef.current || new Set();
    const visibleMembers = (currentMembers || []).filter((m) => !hidden.has(getMemberMapId(m)));
    updateMemberMarkersOnMap(mapRef.current, visibleMembers, {
      markersRef,
      activeMemberId,
      highlightedMemberIds,
      showMemberNames,
      onlineTimeoutMs: memberTempOfflineTimeoutMs,
      offlineTimeoutMs: memberOfflineTimeoutMs,
      presenceById: presenceByIdRef.current,
      speedAuto: memberMarkerSpeedAuto,
      // globe 期间跳过绘制后，切回 mercator 时用最新列表补画（避免刷新后有轨迹无人点）
      getMembers: () => {
        const hidden = hiddenMapMemberIdsRef.current || new Set();
        const list = filteredMembersRef.current || membersRef.current || [];
        return (list || []).filter((m) => !hidden.has(getMemberMapId(m)));
      },
      // 点击图标：飞行 + 复制/删除；仅轻量 App 可再开指挥对话
      onFlyTo: (member) => {
        flyToMemberRef.current?.(member);
      },
      onRemoveFromMap: (member) => {
        toggleMemberMapHidden(member);
        message.success(`已从地图移除 ${member?.name || '该点位'}`);
      },
      onOpenChat: (member) => {
        openMemberChatRef.current?.(member);
      }
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/track/settings');
        const data = await res.json();
        if (cancelled || !data?.success) return;
        const n = parseInt(data.data?.realtime_track_offline_grace_minutes, 10);
        if (Number.isFinite(n) && n > 0) setRealtimeTrackOfflineGraceMinutes(n);
        const tempRaw = data.data?.member_temp_offline_minutes ?? data.data?.member_online_minutes;
        const tempMin = parseInt(tempRaw, 10);
        if (Number.isFinite(tempMin) && tempMin >= 1) setMemberTempOfflineMinutes(tempMin);
        const offlineMin = parseInt(data.data?.member_offline_minutes, 10);
        if (Number.isFinite(offlineMin) && offlineMin >= 2) setMemberOfflineMinutes(offlineMin);
        if (data.data?.member_marker_speed_auto === false
          || data.data?.member_marker_speed_auto === 'false') {
          setMemberMarkerSpeedAuto(false);
        } else {
          setMemberMarkerSpeedAuto(true);
        }
      } catch (_) { /* 使用默认 */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // 在线阈值 / 速度图标开关变化后立即重绘队员标记
  useEffect(() => {
    updateMarkers(membersRef.current || members || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberTempOfflineTimeoutMs, memberOfflineTimeoutMs, memberMarkerSpeedAuto]);

  const {
    realTimeTrackDrawing,
    realTimeTrackData,
    showRealtimeTracks,
    startRealTimeTrackDrawing: startRealTimeTrackDrawingRaw,
    stopTrackFeatures,
    clearRealtimeTrack,
    hideRealtimeTrack,
    setRealtimeTrackVisible,
    seedRealtimeTrackPoints,
    ensureMissionRecordTrackDrawing
  } = useDashboardRealtimeTracks({
    getMap: () => mapRef.current,
    members,
    membersRef,
    refreshInterval,
    offlineGraceMinutes: realtimeTrackOfflineGraceMinutes,
    trackColors
  });

  // 「绘制轨迹」：仅根据队员持续上报的实时点位连线（不读设备历史轨迹）；需手动点按钮
  const startRealTimeTrackDrawing = useCallback(async (memberName) => {
    await startRealTimeTrackDrawingRaw(memberName);
    message.success(`已按实时点位开始绘制 ${memberName} 的轨迹`);
  }, [startRealTimeTrackDrawingRaw]);

  const { fetchDevices } = useDashboardFetchDevices({
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    trackModeApplied,
    startTimeRef,
    endTimeRef,
    membersRef,
    setMembers,
    setLastRefreshTime,
    setRefreshStatus,
    setNetworkQuality,
    onDevicesFetched: notifyDevicesFetched,
    getAppDisplayDays: () => {
      const n = Number(mapProviderSettingsRef.current?.appMap?.trackDisplayDays);
      return Number.isFinite(n) ? n : 7;
    }
  });

  useDashboardAppTrackAutoload({
    members,
    setTrackData,
    setShowTracks,
    trackDisplayDays: mapProviderSettings?.appMap?.trackDisplayDays ?? 7
  });

  // 救援数据加载/上图/列表/清理（已拆至 utils/dashboard/dashboardRescueActions.js）
  const {
    loadRescueData,
    displayRescueTargetOnMap,
    displayRescueTrackOnMap,
    showRescueTargetList,
    showRescueTargetsList,
    showRescueTracksList,
    clearRescueData,
    removeRescueTargetFromMap,
    removeRescueTrackFromMap
  } = createDashboardRescueActions({
    mapRef,
    rescueDisplayLngLatCacheRef,
    rescueTargetMarkersRef,
    rescueTrackLinesRef,
    rescueTargets,
    rescueTracks,
    setRescueTargets,
    setRescueTracks,
    commandPinsMapRef: commandPinsMapBridgeRef
  });

  const {
    plantRescueSearchTarget,
    rescueSearchTargetPushModalProps
  } = useDashboardRescueSearchTargetPush({
    getMap: () => mapRef.current,
    displayRescueTargetOnMap,
    loadRescueData
  });


  const fetchTrack = async (name, options = {}) => {
    const result = await fetchMemberHistoryTrack({
      name,
      trackData,
      forceRefresh: options.forceRefresh !== false,
      findMember: (n) => filteredMembers.find(m => m.name === n) || members.find(m => m.name === n),
      trackMode,
      trackMinutes,
      trackHours,
      trackDays,
      startTime: startTimeRef.current || '',
      endTime: endTimeRef.current || '',
      playbackRange,
      explicitRange: options.explicitRange || null,
      apiFetch
    });
    if (result.action === 'success') {
      setTrackData(prev => ({ ...prev, [name]: result.track }));
      setShowTracks(prev => ({ ...prev, [name]: true }));
      const label = options.timeLabel ? `（${options.timeLabel}）` : '';
      const usable = trackHasUsableCoordinates(result.track);
      if (!result.track?.length) {
        message.info(result.message || `${name}${label} 暂无轨迹点（单兵需先上报，点会写入服务器后再显示）`);
      } else if (!usable) {
        message.warning(`已获取 ${name}${label} ${result.track.length} 个点，但坐标无效（占位/越界），无法上图`);
      } else if (options.showSuccess) {
        message.success(`已获取 ${name}${label} 轨迹 ${result.track.length} 个点`);
      }
      // 手动获取轨迹：退出地球全景并飞到轨迹，避免「有点数但看不见」
      if (result.track?.length > 0 && options.flyTo !== false) {
        const paintFetchedTrack = () => {
          const map = mapRef.current;
          if (!map) return;
          if (usable) {
            try {
              stopEarthAnimation?.();
            } catch (_) {
              /* ignore */
            }
          }
          const paintResult = renderHistoryTracksOnMap(map, {
            trackData: { ...trackData, [name]: result.track },
            showTracks: { ...showTracks, [name]: true },
            trackColors,
            members
          });
          if (!usable) return;
          if (paintResult?.deferred) {
            map.once?.('idle', () => {
              renderHistoryTracksOnMap(map, {
                trackData: { ...trackData, [name]: result.track },
                showTracks: { ...showTracks, [name]: true },
                trackColors,
                members
              });
              fitMapToHistoryTrack(map, result.track);
            });
            return;
          }
          fitMapToHistoryTrack(map, result.track);
        };
        setTimeout(paintFetchedTrack, 0);
      }
    } else if (result.action === 'fail') {
      message.warning(result.message);
    } else if (result.action === 'error') {
      message.error(result.message);
    }
    return result;
  };

  const resolveDownloadTimeRange = (preset, range) => resolveDownloadTimeRangeUtil(preset, range, dayjs);

  // 「获取轨迹」：先选天数/起止时间，再拉取设备已上传历史轨迹
  const handleFetchMemberTrack = (member) => {
    if (!member?.name) {
      message.warning('队员信息不存在');
      return;
    }
    setTrackDownloadModal({
      visible: true,
      mode: 'fetch',
      member,
      preset: 'today',
      range: [dayjs().startOf('day'), dayjs().endOf('day')],
      loading: false
    });
  };

  // 打开下载轨迹弹窗（默认选中当天）
  const openDownloadTrackModal = (member) => {
    if (!member) {
      message.warning('队员信息不存在');
      return;
    }
    setTrackDownloadModal({
      visible: true,
      mode: 'download',
      member,
      preset: 'today',
      range: [dayjs().startOf('day'), dayjs().endOf('day')],
      loading: false
    });
  };

  const { trackLineActionProps } = useDashboardTrackLineActions({
    getMap: () => mapRef.current,
    enabled: isLoggedIn && authed,
    realTimeTrackData,
    realTimeTrackDrawing,
    trackData,
    members,
    openDownloadTrackModal,
    stopTrackFeatures,
    clearRealtimeTrack,
    hideRealtimeTrack,
    setShowTracks,
    onSendLocation: (...args) => openComposeAtWgsRef.current?.wgs?.(...args),
    onSendLocationDisplay: (...args) => openComposeAtWgsRef.current?.display?.(...args)
  });

  // 弹窗确认：获取轨迹 或 下载轨迹
  const confirmDownloadMemberTrack = async () => {
    const { member, preset, range, mode } = trackDownloadModal;
    if (!member) return;

    const timeRange = resolveDownloadTimeRange(preset, range);
    if (!timeRange) {
      message.warning('请选择有效的自定义时间段');
      return;
    }

    setTrackDownloadModal((prev) => ({ ...prev, loading: true }));

    if (mode === 'fetch') {
      try {
        message.loading({
          content: `正在获取 ${member.name}（${timeRange.label}）轨迹…`,
          key: 'fetchTrack',
          duration: 0
        });
        const result = await fetchTrack(member.name, {
          forceRefresh: true,
          explicitRange: { start: timeRange.start, end: timeRange.end },
          timeLabel: timeRange.label,
          showSuccess: true
        });
        message.destroy('fetchTrack');
        if (result?.action === 'success' || result?.action === 'fail' || result?.action === 'error') {
          setTrackDownloadModal((prev) => ({ ...prev, visible: false, loading: false }));
        } else {
          setTrackDownloadModal((prev) => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('获取轨迹失败:', error);
        message.error({ content: `获取轨迹失败: ${error.message || '未知错误'}`, key: 'fetchTrack' });
        setTrackDownloadModal((prev) => ({ ...prev, loading: false }));
      }
      return;
    }

    try {
      message.loading({ content: `正在导出 ${member.name}（${timeRange.label}）轨迹...`, key: 'trackDownload', duration: 0 });
      const result = await downloadMemberTrackAsKml({
        member,
        timeRange,
        fetchFn: apiFetch,
        dayjsLib: dayjs
      });
      if (result.insufficient) {
        message.warning({
          content: `${member.name} 在「${timeRange.label}」暂无足够轨迹点（至少需要2个点）`,
          key: 'trackDownload'
        });
        return;
      }
      const fmtText = (result.formats || ['kml']).map((f) => f.toUpperCase()).join('+');
      message.success({
        content: `已导出 ${member.name} 轨迹（${timeRange.label}，${result.points} 点，${fmtText}，可导入谷歌地球）`,
        key: 'trackDownload'
      });
      setTrackDownloadModal((prev) => ({ ...prev, visible: false, loading: false }));
    } catch (error) {
      console.error('下载轨迹失败:', error);
      message.error({ content: `下载轨迹失败: ${error.message || '未知错误'}`, key: 'trackDownload' });
      setTrackDownloadModal((prev) => ({ ...prev, loading: false }));
    } finally {
      setTrackDownloadModal((prev) => (prev.loading ? { ...prev, loading: false } : prev));
    }
  };

  /** 右上角「轨迹」/「查看轨迹」：仅显隐已有轨迹（历史+实时），不自动抓取 */
  const toggleTrack = (name) => {
    const hasHistory = (trackData[name] || []).length > 0;
    const hasRealtime = (realTimeTrackData[name] || []).length > 0;
    const historyVisible = !!showTracks[name];
    const realtimeVisible = hasRealtime && showRealtimeTracks[name] !== false;
    const anyVisible = (hasHistory && historyVisible) || realtimeVisible;

    if (anyVisible) {
      if (hasHistory) setShowTracks((prev) => ({ ...prev, [name]: false }));
      if (hasRealtime) setRealtimeTrackVisible?.(name, false);
      return;
    }

    if (!hasHistory && !hasRealtime) {
      message.info('暂无轨迹可显示，请先「获取轨迹」或「绘制轨迹」');
      return;
    }
    if (hasHistory) setShowTracks((prev) => ({ ...prev, [name]: true }));
    if (hasRealtime) setRealtimeTrackVisible?.(name, true);
  };

  const isMemberTrackVisible = useCallback((name) => {
    const hasHistory = (trackData[name] || []).length > 0;
    const hasRealtime = (realTimeTrackData[name] || []).length > 0;
    const historyVisible = !!showTracks[name];
    const realtimeVisible = hasRealtime && showRealtimeTracks[name] !== false;
    return (hasHistory && historyVisible) || realtimeVisible;
  }, [trackData, realTimeTrackData, showTracks, showRealtimeTracks]);

  // 一键显示/隐藏所有队员轨迹
  const toggleAllTracks = () => {
    const next = !allTracksVisible;
    setAllTracksVisible(next);
    // 若要显示全部，先加载所有轨迹
    if (next) {
      filteredMembers.forEach(m => {
        if (!trackData[m.name]) fetchTrack(m.name);
      });
    }
    // 更新显示状态
    setShowTracks(Object.fromEntries(filteredMembers.map(m => [m.name, next])));
  };

  const fetchTracks = async () => {
    await fetchAllMemberHistoryTracks({
      members,
      trackData,
      forceRefresh: true,
      trackMode,
      trackMinutes,
      trackHours,
      trackDays,
      playbackRange,
      apiFetch,
      onTrack: (name, track) => {
        setTrackData(prev => ({ ...prev, [name]: track }));
      }
    });
  };

  // 手动时间范围变化时清空已加载轨迹；水合写入 start/end 时不得清掉已应用的轨迹模式
  const timeRangeBaselineReadyRef = useRef(false);
  useEffect(() => {
    if (!settingsLoaded) {
      timeRangeBaselineReadyRef.current = false;
      return;
    }
    if (!timeRangeBaselineReadyRef.current) {
      // 设置刚加载完：只建立基线，避免把服务端恢复的 recent_days 等打回「未应用」
      timeRangeBaselineReadyRef.current = true;
      return;
    }
    setTrackData({});
    setShowTracks({});
    setAllTracksVisible(false);
    setTrackModeApplied(false);
  }, [startTime, endTime, settingsLoaded]);

  useEffect(() => {
    const result = resolveTrackModeTypeChange({
      trackMode,
      trackMinutes,
      trackHours,
      trackDays,
      settingsLoaded,
      trackModeApplied
    });
    if (result.action === 'mark-initialized') {
      if (result.shouldMark) window.dashboardInitialized = true;
      return;
    }
    if (result.action === 'require-reapply') {
      setTrackModeApplied(false);
      try { localStorage.removeItem(trackModeLocalKey('dashboard_trackModeApplied')); localStorage.removeItem('dashboard_trackModeApplied'); } catch (_) { /* ignore */ }
      console.log('轨迹模式类型已变化，请点击「设置」应用', {
        trackMode, trackMinutes, trackHours, trackDays
      });
    }
  }, [trackMode, trackMinutes, trackHours, trackDays, settingsLoaded, trackModeApplied]);

  // 渲染所有轨迹线（封装函数，便于复用）
  const renderTracks = () => {
    try {
      if (typeof renderHistoryTracksOnMap !== 'function') {
        console.error('renderHistoryTracksOnMap 不可用', renderHistoryTracksOnMap);
        return;
      }
      renderHistoryTracksOnMap(mapRef.current, { trackData, showTracks, trackColors, members });
    } catch (error) {
      console.error('渲染历史轨迹失败:', error);
    }
  };

  // 切底图后按新坐标系重绘覆盖物（相机飞行由 applyBasemap 统一处理）
  const refreshMapOverlaysAfterBasemap = (basemapIdArg) => {
    const map = mapRef.current;
    if (!map) return false;
    const basemapId = basemapIdArg || currentBasemapIdRef.current;
    setActiveBasemapIdForCoord(basemapId, map);
    try {
      updateMarkers(membersRef.current || members || []);
    } catch (e) {
      console.warn('底图切换后重绘队员标记失败:', e);
    }
    try {
      renderTracks();
    } catch (e) {
      console.warn('底图切换后重绘轨迹失败:', e);
    }
    try {
      loadRescueData?.();
    } catch (e) {
      console.warn('底图切换后重绘救援点失败:', e);
    }
    try {
      // 令牌选点：立即按新底图纠偏已有标记，并再拉一次列表兜底
      reprojectPickPointMarkers(map, pickPoints, pickPointMarkersRef.current, basemapId, {
        canDelete: hasPermission('手机选点删除'),
        onDelete: handleDeletePickPoint
      });
      loadPickPoints?.();
    } catch (e) {
      console.warn('底图切换后重绘令牌选点失败:', e);
    }
    try {
      reprojectFieldMessagePinsOnMapRef.current?.(basemapId);
      loadFieldMessagePinsRef.current?.();
    } catch (e) {
      console.warn('底图切换后重绘消息打点失败:', e);
    }
    try {
      reprojectCommandPinsOnMapRef.current?.(basemapId);
      loadCommandPinsRef.current?.();
    } catch (e) {
      console.warn('底图切换后重绘指令位置点失败:', e);
    }
    try {
      const last = lastCommanderLocationRef.current;
      if (last && Number.isFinite(Number(last.lng)) && Number.isFinite(Number(last.lat))) {
        const display = toMapLngLat(last.lng, last.lat, basemapId, map) || [last.lng, last.lat];
        placeCommanderMarker(map, display[0], display[1], last.accuracy, {
          wgsLng: last.lng,
          wgsLat: last.lat
        });
      }
    } catch (e) {
      console.warn('底图切换后重绘指挥员标记失败:', e);
    }
    // 路网/等高线等矢量叠加：setStyle 会清空，按 registry 恢复并按新底图纠偏
    try {
      whenMapStyleSettled(map, () => {
        Promise.resolve(rehydrateMapLayerOverlays(map)).then((r) => {
          if (r?.restored > 0) {
            console.log(`[map-layer] 切底图后已恢复叠加层 ${r.restored}`);
          }
        }).catch((e) => {
          console.warn('底图切换后恢复地图叠加层失败:', e);
        });
      }, 2000);
    } catch (e) {
      console.warn('底图切换后恢复地图叠加层失败:', e);
    }
    return false;
  };
  refreshMapOverlaysAfterBasemapRef.current = refreshMapOverlaysAfterBasemap;

  // 安全清理地图资源的函数
  const safelyCleanupMap = () => {
    mapRef.current = safelyDestroyMap(mapRef.current, { trackData, members });
  };

  // 页面卸载时清理资源
  useEffect(() => {
    return () => {
      // 清理地图资源
      safelyCleanupMap();
      
      // 清理初始化标记
      if (window.dashboardInitialized) {
        console.log('页面卸载，清理初始化标记');
        delete window.dashboardInitialized;
      }
    };
  }, []);

  // 页面加载时自动加载轨迹数据（恢复设置时只上图，禁止自动飞，避免伪定位打断地球）
  useEffect(() => {
    // 如果队员数据已加载且有显示轨迹的队员，自动加载轨迹数据
    if (members.length > 0 && Object.keys(showTracks).length > 0) {
      Object.keys(showTracks).forEach(memberName => {
        if (showTracks[memberName] && !trackData[memberName]) {
          fetchTrack(memberName, { flyTo: false });
        }
      });
    }
  }, [members, showTracks, trackData]);

  // 清掉用户设置里残留的「伪定位队员」轨迹勾选（如虚拟机1 @ 堪萨斯）
  useEffect(() => {
    if (!members.length || !Object.keys(showTracks).length) return;
    let changed = false;
    const next = { ...showTracks };
    Object.keys(next).forEach((name) => {
      if (!next[name]) return;
      const m = members.find((x) => x.name === name);
      if (!m) return;
      const lat = Number(m.last_latitude);
      const lng = Number(m.last_longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng) && isBogusGpsCoordinate(lat, lng)) {
        delete next[name];
        changed = true;
      }
    });
    if (changed) {
      console.warn('已清除伪定位队员的轨迹显示勾选', Object.keys(showTracks).filter((k) => !next[k]));
      setShowTracks(next);
    }
  }, [members]);

  // 监听队员名字显示状态变化，重新渲染标记
  useEffect(() => {
    if (mapRef.current && members.length > 0) {
      updateMarkers(members);
    }
  }, [showMemberNames]); // 当showMemberNames状态变化时重新渲染

  useDashboardDeviceRefreshEffects({
    settingsLoaded,
    trackModeApplied,
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    refreshInterval,
    fetchDevices,
    requestManager,
    setTrackModeApplied,
    setSettingsLoaded
  });

  useDashboardPerfInit({
    settingsLoaded,
    apiFetch,
    setDevicePerformance,
    setNetworkQuality,
    setRefreshInterval
  });

  useOnlineMemberNotifications({
    members,
    presenceById,
    onlineBaselineReadyRef,
    lastOnlineMembers,
    setLastOnlineMembers,
    setOpenOrg,
    setOpenGroup,
    setOpenMember,
    setHighlightedMemberIds,
    setOnlineNotifications,
    onPresenceTick: () => {
      updateMarkers(membersRef.current || members || []);
    }
  });

  useDashboardSettingsLifecycle({
    settingsLoaded,
    state: {
      startTime,
      endTime,
      hideInvalid,
      showOnlineOnly,
      trackMode,
      trackMinutes,
      trackHours,
      trackDays,
      playbackRange,
      sidebarWidth,
      collapsed,
      showDownload,
      showTracks,
      allTracksVisible,
      trackColors,
      refreshInterval,
      showMemberNames,
      missionMemberIds,
      missionGroups,
      missionMode,
      missions,
      activeMissionId,
      memberPanelTab
    },
    setters: {
      setStartTime,
      setEndTime,
      setHideInvalid,
      setShowOnlineOnly,
      setTrackMode,
      setTrackMinutes,
      setTrackHours,
      setTrackDays,
      setPlaybackRange,
      setSidebarWidth,
      setCollapsed,
      setShowDownload,
      setShowTracks,
      setAllTracksVisible,
      setTrackColors,
      setShowMemberNames,
      setRefreshInterval,
      setTrackModeApplied,
      setSettingsLoaded,
      setMissionMemberIds,
      setMissionGroups,
      setMissionMode,
      setMemberPanelTab,
      setMissions,
      setActiveMissionId
    },
    fetchDevices,
    onShowLeftPanel: () => {
      setShowLeftPanel(true);
      setLeftPanelVisible(true);
    }
  });

  // 流媒体检测定时器
  useEffect(() => {
    // 初始检测
    checkAllStreams();
    
    // 设置定时检测（每30秒检测一次）
    const streamInterval = setInterval(checkAllStreams, 30000);
    
    return () => {
      clearInterval(streamInterval);
    };
  }, [members]); // 当队员列表更新时重新检测

  // 3. useEffect依赖项优化，避免死循环
  // 只在filteredMembers / 隐藏集合 / 在线三态变化时更新地图标记
  useEffect(() => {
    updateMarkers(filteredMembers);
  }, [filteredMembers, activeMemberId, highlightedMemberIds, hiddenMapMemberIds, presenceById]);

  useMemberTreeAutoExpand({
    filteredMembers: listMembers,
    memberTreeHydratedRef,
    setOpenOrg,
    setOpenGroup,
    setOpenMember
  });

  // 保留原有二维 / 卫星 / 高程快捷按钮。
  const mapStyles = DASHBOARD_MAP_STYLES;
  const {
    currentMapStyleId,
    setCurrentMapStyleId,
    styleModeAvailability,
    switchMapStyle,
    renderBasemapLayerControl
  } = useDashboardBasemapControls({
    getMap: () => mapRef.current,
    mapProviderSettings,
    mapProviderSettingsRef,
    currentBasemapId,
    setCurrentBasemapId,
    currentBasemapIdRef,
    activeOverlayIds,
    setActiveOverlayIds,
    activeOverlayIdsRef,
    showBasemapPanel,
    setShowBasemapPanel,
    onBasemapUserPick: () => {
      basemapUserPickedRef.current = true;
    },
    refreshMapOverlaysAfterBasemap
  });
  currentMapStyleIdRef.current = currentMapStyleId;

  const {
    handleTrackerFieldMessage,
    handleTrackerMessagePinRemoved,
    getMessagesForMember,
    appendMemberMessage,
    fieldMessageBanners,
    maxVisibleFieldBanners,
    dismissFieldMessageBanner,
    flyToFieldMessageMember,
    loadFieldMessagePins,
    reprojectFieldMessagePinsOnMap,
    clearFieldMessageBubbles,
    clearMessagesForMembers,
    removeMessageFromMap,
    historyEpoch
  } = useDashboardFieldMessages({
    getMap: () => mapRef.current,
    getMembers: () => membersRef.current || members,
    onOpenMemberChat: openMemberChat,
    onFlyToMember: (member) => flyToMemberRef.current?.(member),
    onVoicePttReceived: (payload) => handleVoicePttReceivedRef.current?.(payload),
    shouldAutoPlayVoicePtt: (payload) => shouldAutoPlayVoiceForMissionContext({
      memberPanelTab: memberPanelTabRef.current,
      activeMissionId: activeMissionIdRef.current,
      missionMemberIds: missionMemberIdsRef.current,
      payload,
      idMatchesMember: missionIdMatchesMember
    }),
    onVoicePttAutoPlayResult: (payload, result) => handleVoicePttAutoPlayResultRef.current?.(payload, result),
    isMemberChatOpen: (payload) => {
      const keys = openChatKeysRef.current;
      if (!keys || keys.size === 0 || !payload) return false;
      if (payload.device_unique_id && keys.has(String(payload.device_unique_id))) return true;
      if (payload.name && keys.has(String(payload.name))) return true;
      return false;
    }
  });
  loadFieldMessagePinsRef.current = loadFieldMessagePins;
  reprojectFieldMessagePinsOnMapRef.current = reprojectFieldMessagePinsOnMap;

  const {
    handleTrackerCommandDispatch,
    handleTrackerCommandPinRemoved,
    placeOutboundCommandPin,
    loadCommandPins,
    reprojectCommandPinsOnMap,
    commandPinsMapRef
  } = useDashboardCommandPins({
    getMap: () => mapRef.current,
    rescueTargetMarkersRef
  });
  // bridge：救援上图用同一份 pins Map（hook 声明较晚）
  commandPinsMapBridgeRef.current = commandPinsMapRef.current;
  loadCommandPinsRef.current = loadCommandPins;
  reprojectCommandPinsOnMapRef.current = reprojectCommandPinsOnMap;

  const {
    plantTarget,
    planted,
    sendingLocation,
    composeOpen,
    startSendLocation,
    confirmSendLocation,
    cancelSendLocation,
    closeSendLocationCompose,
    submitSendLocationCompose,
    openComposeAtWgs,
    openComposeAtDisplay
  } = useDashboardSendLocation({
    getMap: () => mapRef.current,
    getBasemapId: () => currentBasemapIdRef.current,
    drawingToolsActiveRef,
    onOutboundSent: (entry) => {
      appendMemberMessage(entry);
      placeOutboundCommandPin(entry);
    },
    onRescueTargetSent: (target, opts) => {
      if (!target) return;
      if (commandPinCoversLatLng(commandPinsMapBridgeRef.current, target.lat, target.lng)) return;
      if (target.id != null && opts?.displayLngLat) {
        rescueDisplayLngLatCacheRef.current.set(String(target.id), opts.displayLngLat);
      }
      displayRescueTargetOnMapUtil(mapRef.current, target, {
        markersRef: rescueTargetMarkersRef,
        onHoverShowList: showRescueTargetList,
        displayLngLat: opts?.displayLngLat
      });
    }
  });
  openComposeAtWgsRef.current = {
    wgs: openComposeAtWgs,
    display: openComposeAtDisplay
  };

  /** App 队员自助修改姓名/小组：顶部提示 + 展开新所在树节点 */
  const handleTrackerIdentityUpdate = useCallback((payload) => {
    if (!payload?.device_unique_id) return;
    const notification = buildIdentityUpdateNotification(payload);
    if (notification) {
      setOnlineNotifications((prev) => [...prev, notification].slice(-8));
      setTimeout(() => {
        setOnlineNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      }, 8000);
      // 额外弹一次全局提示，避免地图顶栏被挡住时看不到
      message.info({
        content: `${notification.memberName} 修改了设备信息${notification.detail ? `：${notification.detail}` : ''}`,
        duration: 5
      });
    }
    const org = payload.org_name || '未知组织';
    const group = payload.group_name || '未知小组';
    const memberKey = payload.device_unique_id;
    setOpenOrg((prev) => ({ ...prev, [org]: true }));
    setOpenGroup((prev) => ({ ...prev, [group]: true }));
    // 改名/身份更新：不自动展开队员详情
    setHighlightedMemberIds((prev) => {
      const next = new Set(prev);
      next.add(memberKey);
      return next;
    });
    // 立刻按服务器权威数据刷新树，避免仅靠 WS 补丁时页面仍是旧分包
    fetchDevices?.().catch?.(() => {});
    setTimeout(() => {
      updateMarkers?.(membersRef.current || []);
    }, 0);
  }, [updateMarkers, fetchDevices]);

  useDashboardMapWsBootstrap({
    enabled: isLoggedIn && authed,
    mapContainer,
    mapRef,
    shouldUseEarthOverviewRef,
    isAnimationActiveRef,
    mapStyleReadyRef,
    drawingToolsActiveRef,
    membersRef,
    flyToLatestMemberRef,
    earthAnimationRef,
    startEarthAnimationRef,
    currentBasemapIdRef,
    mapProviderSettingsRef,
    activeOverlayIdsRef,
    setCurrentMapStyleId,
    setMapLoadingState,
    getStyleId: () => currentMapStyleIdRef.current,
    updateMarkers,
    renderTracks,
    loadRescueData,
    loadPickPoints,
    getMembers: () => membersRef.current || members,
    settingsLoaded,
    trackModeApplied,
    fetchDevices,
    setMembers,
    displayPickPointOnMap,
    removePickPointFromMap,
    setPickPoints,
    displayRescueTargetOnMap,
    removeRescueTargetFromMap,
    setRescueTargets,
    displayRescueTrackOnMap,
    removeRescueTrackFromMap,
    setRescueTracks,
    stopEarthAnimation,
    clearRescueData,
    pickPointMarkersRef,
    onTrackerFieldMessage: handleTrackerFieldMessage,
    onTrackerMessagePinRemoved: handleTrackerMessagePinRemoved,
    onTrackerIdentityUpdate: handleTrackerIdentityUpdate,
    onTrackerCommandDispatch: handleTrackerCommandDispatch,
    onTrackerCommandPinRemoved: handleTrackerCommandPinRemoved,
    onCommandMissionBoardUpdated: async () => {
      try {
        const shared = await loadCommandMissionState(null);
        if (!shared) return;
        const migrated = migrateLegacyMissions({
          missions: shared.missions || [],
          activeMissionId: shared.activeMissionId || null,
          missionMemberIds: shared.missionMemberIds || [],
          missionGroups: shared.missionGroups || [],
          missionMode: shared.missionMode
        });
        setMissions(migrated.missions);
        setActiveMissionId(migrated.activeMissionId);
        const active = (migrated.missions || []).find((m) => m.id === migrated.activeMissionId)
          || migrated.missions[0]
          || null;
        setMissionMemberIds(active ? (active.memberIds || []) : []);
        setMissionGroups(active ? (active.groups || []) : []);
        setMissionMode(Boolean(migrated.missions.length && (active?.memberIds || []).length));
      } catch (err) {
        console.warn('[mission] WS board reload failed:', err?.message || err);
      }
    },
    onPickPointSubmitted: handlePickPointSubmitted,
    loadFieldMessagePins,
    loadCommandPins
  });

  // 按组织和小组分组队员
  const orgMap = groupMembersByOrgAndGroup(listMembers);

  useDashboardMapChromeEffects({
    getMap: () => mapRef.current,
    collapsed
  });

  // useEffect里调用renderTracks；样式未就绪时等 idle 再画，避免「已获取但地图无轨迹」
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const paint = () => {
      try {
        const result = renderHistoryTracksOnMap(map, { trackData, showTracks, trackColors, members });
        return result;
      } catch (error) {
        console.error('渲染历史轨迹失败:', error);
        return null;
      }
    };

    const first = paint();
    if (first?.deferred) {
      const onReady = () => {
        paint();
      };
      map.once?.('idle', onReady);
      map.once?.('style.load', onReady);
      return () => {
        try {
          map.off?.('idle', onReady);
          map.off?.('style.load', onReady);
        } catch (_) {
          /* ignore */
        }
      };
    }
    return undefined;
  }, [showTracks, trackData, trackColors, members]);

  // 监听地图缩放/移动，更新离地高度与中心海拔；mousemove 防抖更新指针海拔
  // 地球自转期间不计算；退出自转 / 切换底图后再按 WGS84 精确查询
  useEffect(() => {
    let cancelled = false;
    let debounceTimer = null;
    let pointerTimer = null;
    let attachedMap = null;
    let pointerSeq = 0;

    const markPaused = () => {
      setMapStatus((prev) => ({
        ...prev,
        height: null,
        elevation: null,
        pointerElevation: null,
        heightStatus: 'paused',
        elevationStatus: 'paused',
        pointerElevationStatus: 'paused'
      }));
    };

    const refreshMapStatus = (opts = {}) => {
      const map = mapRef.current;
      if (!map || cancelled) return;
      if (isAnimationActiveRef.current || isEarthAnimationActive) {
        markPaused();
        return;
      }

      try {
        const zoom = map.getZoom();
        const center = map.getCenter();
        const height = estimateViewHeightUtil(
          zoom,
          center.lat,
          mapContainer.current?.offsetHeight || 800
        );
        setMapStatus((prev) => ({
          ...prev,
          zoom,
          center: [center.lng, center.lat],
          height,
          heightStatus: 'ok',
          elevationStatus: 'loading'
        }));

        mapStateManager.saveMapState('dashboard', {
          center: [center.lng, center.lat],
          zoom,
          style: currentMapStyleIdRef.current
        });

        // 海拔 API 使用 WGS84：高德/谷歌底图中心需先转回 WGS84
        const wgs = fromMapLngLat(center.lng, center.lat, currentBasemapId, map)
          || [center.lng, center.lat];
        fetchOpenElevation(wgs[0], wgs[1], fetch, { force: !!opts.force }).then((elevation) => {
          if (cancelled || isAnimationActiveRef.current) return;
          if (elevation == null) {
            setMapStatus((prev) => ({ ...prev, elevationStatus: 'unavailable' }));
            return;
          }
          setMapStatus((prev) => ({
            ...prev,
            elevation,
            elevationStatus: 'ok'
          }));
        });
      } catch (_) {
        /* ignore */
      }
    };

    const queryPointerElevation = (lngLat) => {
      const map = mapRef.current;
      if (!map || !lngLat || cancelled) return;
      if (isAnimationActiveRef.current || isEarthAnimationActive) return;

      const wgs = fromMapLngLat(lngLat.lng, lngLat.lat, currentBasemapId, map)
        || [lngLat.lng, lngLat.lat];
      const seq = ++pointerSeq;
      setMapStatus((prev) => ({
        ...prev,
        pointerElevationStatus: prev.pointerElevation != null ? prev.pointerElevationStatus : 'loading'
      }));

      fetchOpenElevation(wgs[0], wgs[1], fetch).then((elevation) => {
        if (cancelled || seq !== pointerSeq || isAnimationActiveRef.current) return;
        if (elevation == null) {
          // 限流/失败：保留上次读数；尚无读数则显示 —
          setMapStatus((prev) => ({
            ...prev,
            pointerElevationStatus: prev.pointerElevation != null ? 'ok' : 'idle'
          }));
          return;
        }
        setMapStatus((prev) => ({
          ...prev,
          pointerElevation: elevation,
          pointerElevationStatus: 'ok'
        }));
      });
    };

    refreshMapStatusRef.current = refreshMapStatus;

    const onMoveEnd = (e) => {
      if (e?.zhxtEarthSpin || isAnimationActiveRef.current) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => refreshMapStatus(), 220);
    };

    const onMouseMove = (e) => {
      if (isAnimationActiveRef.current || isEarthAnimationActive) return;
      if (!e?.lngLat) return;
      clearTimeout(pointerTimer);
      // 停稳约 400ms 再查，配合全局 2s 限流，避免刷外部 API
      const ll = e.lngLat;
      pointerTimer = setTimeout(() => queryPointerElevation(ll), 400);
    };

    const tryAttach = () => {
      const map = mapRef.current;
      if (!map) return false;
      if (map === attachedMap) return true;
      if (attachedMap) {
        attachedMap.off('moveend', onMoveEnd);
        attachedMap.off('zoomend', onMoveEnd);
        attachedMap.off('mousemove', onMouseMove);
      }
      attachedMap = map;
      map.on('moveend', onMoveEnd);
      map.on('zoomend', onMoveEnd);
      map.on('mousemove', onMouseMove);
      refreshMapStatus({ force: true });
      return true;
    };

    const pollId = setInterval(() => {
      if (tryAttach()) clearInterval(pollId);
    }, 300);
    tryAttach();

    if (isEarthAnimationActive) {
      markPaused();
    } else {
      refreshMapStatus({ force: true });
    }

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(debounceTimer);
      clearTimeout(pointerTimer);
      refreshMapStatusRef.current = () => {};
      if (attachedMap) {
        attachedMap.off('moveend', onMoveEnd);
        attachedMap.off('zoomend', onMoveEnd);
        attachedMap.off('mousemove', onMouseMove);
      }
    };
  }, [isEarthAnimationActive, currentBasemapId, currentMapStyleId]);

  // 页面加载时获取最新公告
  useEffect(() => {
    const fetchAnnouncement = async () => {
      const { announcement: ann, show } = await fetchPublicAnnouncementState();
      setAnnouncement(ann);
      setShowAnnouncement(show);
    };

    fetchAnnouncement();
    const interval = setInterval(fetchAnnouncement, 60000);
    return () => clearInterval(interval);
  }, []);

  // 关闭公告
  const closeAnnouncement = () => {
    closePublicAnnouncement(announcement);
    setShowAnnouncement(false);
  };

  // 判断是否为移动端
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    locateCommander,
    handleResetDashboardConfig: handleResetDashboardConfigBase,
    handleReplayEarth,
    handleShowOnlineTracks,
    handleDrawOnlineTracks,
    handleRefreshIntervalChange,
    flyToMember,
    trackModeAndPlaybackUI
  } = useDashboardToolbarActions({
    getMap: () => mapRef.current,
    members,
    membersRef,
    trackMode,
    trackMinutes,
    trackHours,
    trackDays,
    playbackRange,
    isPlaying,
    setIsPlaying,
    setPlaybackIndex,
    setTrackModeApplied,
    setStartTime,
    setEndTime,
    setHideInvalid,
    setShowOnlineOnly,
    setTrackMode,
    setTrackMinutes,
    setTrackHours,
    setTrackDays,
    setPlaybackRange,
    setRefreshInterval,
    showTracks,
    toggleTrack,
    startRealTimeTrackDrawing,
    fetchDevices,
    fetchTracks,
    trackData,
    playbackTimerRef,
    isAnimationActiveRef,
    shouldUseEarthOverviewRef,
    stopEarthAnimation,
    getBasemapId: () => currentBasemapIdRef.current,
    onCommanderLocated: (record) => {
      lastCommanderLocationRef.current = record;
      writeLastCommanderLocationToStorage(record);
    }
  });

  const handleResetDashboardConfig = useCallback(() => {
    const endingIds = (missions || []).map((m) => m?.id).filter(Boolean);
    handleResetDashboardConfigBase?.();
    setMissionMode(false);
    setMissionMemberIds([]);
    setMissionPickIds([]);
    setMissionGroups([]);
    setMissions([]);
    setActiveMissionId(null);
    setMemberPanelTab('all');
    persistMissionState({
      missionMemberIds: [],
      missionGroups: [],
      missionMode: false,
      memberPanelTab: 'all',
      missions: [],
      activeMissionId: null
    });
    if (endingIds.length) {
      endMissionsRosterOnServer(endingIds).catch((rosterErr) => {
        console.warn('[mission] end all rosters on reset failed:', rosterErr?.message || rosterErr);
      });
    }
  }, [handleResetDashboardConfigBase, missions]);

  const resolveMissionMembersByIds = useCallback((ids) => {
    const want = Array.isArray(ids) ? ids : [];
    const pool = membersRef.current || [];
    return (pool || []).filter((m) => want.some((id) => missionIdMatchesMember(id, m) || id === getMemberExpandKey(m)));
  }, []);

  const syncVoicePttForMembers = useCallback(async (memberList, allow) => {
    try {
      const targets = collectVoicePttTargets(memberList);
      const data = await setMissionVoicePttAllow({ allow: Boolean(allow), ...targets });
      return data;
    } catch (err) {
      console.warn('[mission] allow_voice_ptt sync failed:', err?.message || err);
      message.warning(liveVoiceErrorMessage('VOICE_PTT_ALLOW_FAILED', allow ? '已加入任务，但对讲门禁同步失败，请稍后重试' : '收回对讲权限失败'));
      return { success: false, error: err?.message || String(err) };
    }
  }, []);

  const syncTrackRecordForMembers = useCallback(async (memberList, allow, missionId = null) => {
    try {
      const targets = collectVoicePttTargets(memberList);
      if (!(targets.deviceUniqueIds || []).length && !(targets.names || []).length) {
        return { success: true, updated_count: 0, skipped: true };
      }
      return await setMissionRecordTracks({
        allow: Boolean(allow),
        deviceUniqueIds: targets.deviceUniqueIds,
        names: targets.names,
        missionId
      });
    } catch (err) {
      console.warn('[mission] mission_record_tracks sync failed:', err?.message || err);
      message.warning(allow
        ? '已开启记录轨迹，但单兵策略同步失败，请稍后重试'
        : '关闭任务记录轨迹策略失败');
      return { success: false, error: err?.message || String(err) };
    }
  }, []);

  const trackRecordScopeSigRef = useRef('');

  // 项 8：missions[] 为权威 store；active 的 memberIds/groups 仅为当前标签派生视图，写入须经 flush + persistMissionsSnapshot
  const flushActiveMissionIntoList = useCallback((list, ids, groups, aid) => {
    const arr = Array.isArray(list) ? list : [];
    const mid = aid || activeMissionId;
    if (!mid) return arr;
    return arr.map((m) => (
      m.id === mid
        ? { ...m, memberIds: Array.isArray(ids) ? ids : [], groups: Array.isArray(groups) ? groups : [] }
        : m
    ));
  }, [activeMissionId]);

  const ensureActiveMissionList = useCallback((list, ids, groups) => {
    let arr = flushActiveMissionIntoList(list, ids, groups, activeMissionId);
    let aid = activeMissionId;
    if (!aid || !arr.some((m) => m.id === aid)) {
      const created = createEmptyMission(arr);
      arr = [...arr, created];
      aid = created.id;
    }
    return { list: arr, activeId: aid };
  }, [activeMissionId, flushActiveMissionIntoList]);

  const persistMissionsSnapshot = useCallback((list, aid, ids, groups, mode, tab) => {
    const missionsList = Array.isArray(list) ? list : [];
    const activeId = aid || null;
    const active = getActiveMission(missionsList, activeId);
    const memberIds = Array.isArray(ids) ? ids : (active?.memberIds || []);
    const g = Array.isArray(groups) ? groups : (active?.groups || []);
    persistMissionState({
      missionMemberIds: memberIds,
      missionGroups: g,
      missionMode: Boolean(mode),
      memberPanelTab: tab || memberPanelTab,
      missions: missionsList,
      activeMissionId: activeId
    });
    // 同步通联名册到服务端（debounce；失败只 warn）
    try {
      scheduleMissionsRosterSync(missionsList, membersRef.current || members || []);
    } catch (rosterErr) {
      console.warn('[mission] schedule roster sync failed:', rosterErr?.message || rosterErr);
    }
  }, [memberPanelTab, members]);

  const handleToggleMissionRecordTracks = useCallback(async (missionId, enabled) => {
    const mid = String(missionId || '').trim();
    if (!mid) return;
    const flushed = flushActiveMissionIntoList(missions, missionMemberIds, missionGroups, activeMissionId);
    if (!flushed.some((m) => m.id === mid)) {
      message.warning('未找到该任务');
      return;
    }
    const nextList = replaceMission(flushed, mid, { recordTracks: Boolean(enabled) });
    setMissions(nextList);
    persistMissionsSnapshot(nextList, activeMissionId, missionMemberIds, missionGroups, missionMode, memberPanelTab);
    message.success(enabled
      ? '已开启任务记录轨迹：范围内队员将自动续画（大空隙分段）'
      : '已关闭该任务级记录轨迹');
  }, [missions, missionMemberIds, missionGroups, activeMissionId, missionMode, memberPanelTab, flushActiveMissionIntoList, persistMissionsSnapshot]);

  const handleSelectMission = useCallback((id) => {
    if (!id || id === activeMissionId) return;
    const flushed = flushActiveMissionIntoList(missions, missionMemberIds, missionGroups, activeMissionId);
    const next = flushed.find((m) => m.id === id);
    if (!next) return;
    setMissions(flushed);
    setActiveMissionId(id);
    setMissionMemberIds(next.memberIds || []);
    setMissionGroups(next.groups || []);
    setMissionMode((next.memberIds || []).length > 0);
    setMemberPanelTab('mission');
    persistMissionsSnapshot(flushed, id, next.memberIds || [], next.groups || [], (next.memberIds || []).length > 0, 'mission');
    // 切到该任务：有未读则提示；过期不偷偷播
    try { tipUnreadVoicesForMemberIdsRef.current?.(next.memberIds || []); } catch (_) { /* ignore */ }
  }, [activeMissionId, missions, missionMemberIds, missionGroups, flushActiveMissionIntoList, persistMissionsSnapshot]);

  const handleCreateMission = useCallback(() => {
    const flushed = flushActiveMissionIntoList(missions, missionMemberIds, missionGroups, activeMissionId);
    const created = createEmptyMission(flushed);
    const nextList = [...flushed, created];
    setMissions(nextList);
    setActiveMissionId(created.id);
    setMissionMemberIds([]);
    setMissionGroups([]);
    setMissionMode(false);
    setMemberPanelTab('mission');
    persistMissionsSnapshot(nextList, created.id, [], [], false, 'mission');
    message.success('\u5df2\u65b0\u5efa ' + created.name);
  }, [missions, missionMemberIds, missionGroups, activeMissionId, flushActiveMissionIntoList, persistMissionsSnapshot]);

  const handleRenameMission = useCallback((id, name) => {
    const n = String(name || '').trim();
    if (!id || !n) return;
    const flushed = flushActiveMissionIntoList(missions, missionMemberIds, missionGroups, activeMissionId);
    const nextList = replaceMission(flushed, id, { name: n });
    setMissions(nextList);
    persistMissionsSnapshot(nextList, activeMissionId, missionMemberIds, missionGroups, missionMode, memberPanelTab);
  }, [missions, missionMemberIds, missionGroups, activeMissionId, missionMode, memberPanelTab, flushActiveMissionIntoList, persistMissionsSnapshot]);

  const handleCloseMissionTab = useCallback(async (id) => {
    if (!id) return;
    const flushed = flushActiveMissionIntoList(missions, missionMemberIds, missionGroups, activeMissionId);
    const target = flushed.find((m) => m.id === id);
    if (!target) return;
    const liveN = liveCountForMission(target, membersRef.current || members || []);
    if ((target.memberIds || []).length > 0) {
      const ok = window.confirm('\u4efb\u52a1\u300c' + (target.name || id) + '\u300d\u4ecd\u6709 ' + (liveN || target.memberIds.length) + ' \u540d\u6210\u5458\uff0c\u786e\u5b9a\u5173\u95ed\uff1f');
      if (!ok) return;
    }
    const leaving = resolveMissionMembersByIds(target.memberIds || []);
    const revoke = leaving.filter((m) => {
      const key = m?.device_unique_id || m?.name;
      return key && !memberInOtherMissions(key, flushed, id);
    });
    const nextList = removeMission(flushed, id);
    let nextActive = activeMissionId;
    let nextIds = missionMemberIds;
    let nextGroups = missionGroups;
    if (id === activeMissionId) {
      nextActive = nextList[0]?.id || null;
      const active = getActiveMission(nextList, nextActive);
      nextIds = active?.memberIds || [];
      nextGroups = active?.groups || [];
    }
    setMissions(nextList);
    setActiveMissionId(nextActive);
    setMissionMemberIds(nextIds);
    setMissionGroups(nextGroups);
    const nextMode = nextIds.length > 0;
    setMissionMode(nextMode);
    if (!nextList.length) setMemberPanelTab('all');
    persistMissionsSnapshot(nextList, nextActive, nextIds, nextGroups, nextMode, nextList.length ? 'mission' : 'all');
    try {
      await endMissionRosterOnServer(id);
    } catch (rosterErr) {
      console.warn('[mission] end roster on close failed:', rosterErr?.message || rosterErr);
    }
    if (revoke.length) await syncVoicePttForMembers(revoke, false);
  }, [missions, missionMemberIds, missionGroups, activeMissionId, members, flushActiveMissionIntoList, persistMissionsSnapshot, resolveMissionMembersByIds, syncVoicePttForMembers]);

  const handleJoinMission = useCallback(async (pickIds) => {
    const rawIncoming = Array.isArray(pickIds) ? pickIds.filter(Boolean) : [];
    // Never persist bare stream_key / hex tokens as mission people
    const incoming = sanitizeMissionMemberIds(rawIncoming, membersRef.current || members || []);
    if (!incoming.length) {
      message.info('\u8bf7\u5148\u52fe\u9009\u8981\u52a0\u5165\u4efb\u52a1\u7684\u961f\u5458');
      return;
    }
    const ensured = ensureActiveMissionList(missions, missionMemberIds, missionGroups);
    let nextList = ensured.list;
    const aid = ensured.activeId;
    const cur = nextList.find((m) => m.id === aid);
    const prevIds = cur?.memberIds || [];
    const merged = Array.from(new Set([...(prevIds || []), ...incoming]));
    const newly = incoming.filter((id) => !(prevIds || []).includes(id));
    const nextGroups = cur?.groups || missionGroups || [];
    nextList = replaceMission(nextList, aid, { memberIds: merged, groups: nextGroups });
    setMissions(nextList);
    setActiveMissionId(aid);
    setMissionMemberIds(merged);
    setMissionGroups(nextGroups);
    setMissionMode(true);
    setMissionPickIds([]);
    setMemberPanelTab('mission');
    persistMissionsSnapshot(nextList, aid, merged, nextGroups, true, 'mission');
    const newMembers = resolveMissionMembersByIds(newly);
    const syncResult = await syncVoicePttForMembers(newMembers, true);
    if (syncResult && syncResult.success === false) {
      message.warning('\u5df2\u52a0\u5165\u4efb\u52a1 ' + (newly.length || incoming.length) + ' \u4eba\uff0c\u4f46\u5bf9\u8bb2\u95e8\u7981\u672a\u5199\u5165\u4ee4\u724c');
    } else {
      message.success('\u5df2\u52a0\u5165\u5f53\u524d\u4efb\u52a1 ' + (newly.length || incoming.length) + ' \u4eba');
    }
  }, [missions, missionMemberIds, missionGroups, ensureActiveMissionList, persistMissionsSnapshot, resolveMissionMembersByIds, syncVoicePttForMembers]);

  const handleRemoveMissionMember = useCallback(async (memberId) => {
    if (!memberId) return;
    const ensured = ensureActiveMissionList(missions, missionMemberIds, missionGroups);
    let nextList = ensured.list;
    const aid = ensured.activeId;
    const cur = nextList.find((m) => m.id === aid) || { memberIds: [], groups: [] };
    const nextIds = (cur.memberIds || []).filter((id) => !(
      id === memberId
      || missionIdMatchesMember(memberId, { device_unique_id: id, name: id })
      || missionIdMatchesMember(id, { device_unique_id: memberId, name: memberId })
    ));
    const nextGroups = nextIds.length === 0
      ? []
      : (cur.groups || []).map((g) => ({
        ...g,
        memberIds: (g.memberIds || []).filter((id) => !(
          id === memberId
          || missionIdMatchesMember(memberId, { device_unique_id: id, name: id })
          || missionIdMatchesMember(id, { device_unique_id: memberId, name: memberId })
        ))
      }));
    nextList = replaceMission(nextList, aid, { memberIds: nextIds, groups: nextGroups });
    const nextMode = nextIds.length > 0;
    const nextTab = nextMode ? memberPanelTab : (nextList.length ? 'mission' : 'all');
    setMissions(nextList);
    setActiveMissionId(aid);
    setMissionMemberIds(nextIds);
    setMissionGroups(nextGroups);
    setMissionMode(nextMode);
    if (!nextList.length) setMemberPanelTab('all');
    else if (!nextMode && memberPanelTab !== 'mission') setMemberPanelTab(nextTab);
    persistMissionsSnapshot(nextList, aid, nextIds, nextGroups, nextMode, nextTab);
    const removed = resolveMissionMembersByIds([memberId]);
    const revoke = removed.filter((m) => {
      const key = m?.device_unique_id || m?.name;
      return key && !memberInOtherMissions(key, nextList, aid);
    });
    if (revoke.length) await syncVoicePttForMembers(revoke, false);
  }, [missions, missionMemberIds, missionGroups, memberPanelTab, ensureActiveMissionList, persistMissionsSnapshot, resolveMissionMembersByIds, syncVoicePttForMembers]);

  const handleEndMission = useCallback(async () => {
    const flushed = flushActiveMissionIntoList(missions, missionMemberIds, missionGroups, activeMissionId);
    const aid = activeMissionId || flushed[0]?.id;
    if (!aid) {
      message.info('\u5f53\u524d\u6ca1\u6709\u4efb\u52a1');
      return;
    }
    const target = flushed.find((m) => m.id === aid);
    const all = resolveMissionMembersByIds(target?.memberIds || missionMemberIds);
    const revoke = all.filter((m) => {
      const key = m?.device_unique_id || m?.name;
      return key && !memberInOtherMissions(key, flushed, aid);
    });
    const targets = collectVoicePttTargets(revoke);
    const nextList = removeMission(flushed, aid);
    const nextActive = nextList[0]?.id || null;
    const active = getActiveMission(nextList, nextActive);
    const nextIds = active?.memberIds || [];
    const nextGroups = active?.groups || [];
    setMissions(nextList);
    setActiveMissionId(nextActive);
    setMissionMemberIds(nextIds);
    setMissionGroups(nextGroups);
    setMissionPickIds([]);
    const nextMode = nextIds.length > 0;
    setMissionMode(nextMode);
    if (!nextList.length) setMemberPanelTab('all');
    persistMissionsSnapshot(nextList, nextActive, nextIds, nextGroups, nextMode, nextList.length ? 'mission' : 'all');
    try {
      await endMissionRosterOnServer(aid);
    } catch (rosterErr) {
      console.warn('[mission] end roster failed:', rosterErr?.message || rosterErr);
    }
    try { stopVoicePttPlayback(); } catch (_) { /* ignore */ }
    try { clearFieldMessageBubbles?.(); } catch (_) { /* ignore */ }
    try { if (revoke.length) clearMessagesForMembers?.(revoke); } catch (_) { /* ignore */ }
    await syncVoicePttForMembers(revoke, false);
    try {
      const cleaned = await cleanupMissionVoiceJunk({ ...targets, missionId: aid });
      const audioN = Number(cleaned?.audio_objects_deleted || cleaned?.audio_db_deleted || 0);
      const sessN = Number(cleaned?.sessions_ended || 0);
      const msgN = Number(cleaned?.messages_soft_deleted || 0) + Number(cleaned?.commands_soft_deleted || 0);
      message.success('已结束当前任务：回收对讲权限，清理语音会话 ' + sessN + ' / 音频 ' + audioN + ' / 对话 ' + msgN + '（队员轨迹已保留）');
    } catch (err) {
      console.warn('[mission] voice cleanup failed:', err?.message || err);
      message.warning('\u5df2\u7ed3\u675f\u5f53\u524d\u4efb\u52a1\u5e76\u56de\u6536\u5bf9\u8bb2\u6743\u9650\uff0c\u8bed\u97f3\u6e05\u7406\u672a\u5b8c\u6210\uff08\u8f68\u8ff9\u5df2\u4fdd\u7559\uff09');
    }
  }, [missions, missionMemberIds, missionGroups, activeMissionId, flushActiveMissionIntoList, persistMissionsSnapshot, resolveMissionMembersByIds, syncVoicePttForMembers, clearFieldMessageBubbles, clearMessagesForMembers]);

  const handleMissionGroupsChange = useCallback((next) => {
    const resolved = typeof next === 'function' ? next(missionGroups || []) : next;
    const groups = Array.isArray(resolved) ? resolved : [];
    const ensured = ensureActiveMissionList(missions, missionMemberIds, groups);
    const nextList = replaceMission(ensured.list, ensured.activeId, {
      memberIds: missionMemberIds,
      groups
    });
    setMissions(nextList);
    setActiveMissionId(ensured.activeId);
    setMissionGroups(groups);
    persistMissionsSnapshot(nextList, ensured.activeId, missionMemberIds, groups, missionMode, memberPanelTab);
  }, [missions, missionGroups, missionMemberIds, missionMode, memberPanelTab, ensureActiveMissionList, persistMissionsSnapshot]);

  // After hydrate / 队员刷新：共享看板名册 sanitize + allow_voice_ptt 对齐（项 7）
  // 项 11：标签(memberPanelTab)只管过滤；定时刷新 / sanitize / WS 不得改 tab，更不得切回「全部」
  // 勿在 setState updater 里写 keptSnapshot 再同步读取——React 18 下 updater 可能延后执行，keptSnapshot 恒为空会误切 tab
  useEffect(() => {
    if (!settingsLoaded) return undefined;
    let cancelled = false;
    const live = membersRef.current || members || [];
    const prevIds = Array.isArray(missionMemberIdsRef.current) ? missionMemberIdsRef.current : [];
    const keptIds = sanitizeMissionMemberIds(prevIds, live);
    const idsUnchanged = keptIds.length === prevIds.length && keptIds.every((id, i) => id === prevIds[i]);
    if (!idsUnchanged) {
      setMissionMemberIds(keptIds);
    }
    setMissionGroups((prev) => pruneMissionGroups(prev || [], keptIds, live));
    // 不在此改 missionMode / memberPanelTab（由用户点「全部|任务中|回放」显式切换）
    (async () => {
      try {
        const onMission = resolveMissionMembersByIds(keptIds);
        if (cancelled || !onMission.length) return;
        await syncVoicePttForMembers(onMission, true);
      } catch (err) {
        console.warn('[mission] align allow_voice_ptt to roster failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
    // 仅人数变化时重跑；members 引用每分钟刷新会变，但勿因 callback 新身份误切 UI
  }, [settingsLoaded, members.length, resolveMissionMembersByIds, syncVoicePttForMembers]);

  /* multi-mission: bootstrap missions from legacy memberIds */
  useEffect(() => {
    if ((missions || []).length > 0) return;
    if (!(missionMemberIds || []).length && !(missionGroups || []).length) return;
    const created = createEmptyMission([], '\u4efb\u52a1 1');
    created.memberIds = Array.isArray(missionMemberIds) ? missionMemberIds : [];
    created.groups = Array.isArray(missionGroups) ? missionGroups : [];
    setMissions([created]);
    setActiveMissionId(created.id);
    persistMissionsSnapshot([created], created.id, created.memberIds, created.groups, created.memberIds.length > 0, memberPanelTab);
  }, [missions, missionMemberIds, missionGroups, memberPanelTab, persistMissionsSnapshot]);

  /* multi-mission: mirror active ids into missions[] */
  useEffect(() => {
    if (!activeMissionId) return;
    setMissions((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const cur = arr.find((m) => m.id === activeMissionId);
      if (!cur) return arr;
      const ids = Array.isArray(missionMemberIds) ? missionMemberIds : [];
      const groups = Array.isArray(missionGroups) ? missionGroups : [];
      const sameIds = ids.length === (cur.memberIds || []).length && ids.every((id, idx) => id === cur.memberIds[idx]);
      const sameGroups = JSON.stringify(groups) === JSON.stringify(cur.groups || []);
      if (sameIds && sameGroups) return arr;
      return arr.map((m) => (m.id === activeMissionId ? { ...m, memberIds: ids, groups } : m));
    });
  }, [missionMemberIds, missionGroups, activeMissionId]);

  /* 任务/组「记录轨迹」：同步单兵 mission_record_tracks，种子历史密点并自动续画 */
  useEffect(() => {
    if (!settingsLoaded) return undefined;
    let cancelled = false;
    const list = Array.isArray(missions) ? missions : [];
    const scopeIdSet = new Set();
    const sigParts = [];
    list.forEach((m) => {
      if (!m) return;
      const scoped = membersInRecordTrackScope(m);
      scoped.forEach((id) => scopeIdSet.add(id));
      sigParts.push(String(m.id) + ':' + (m.recordTracks === true ? '1' : '0') + ':' + scoped.join(','));
    });
    const sig = sigParts.sort().join(';');
    if (sig === trackRecordScopeSigRef.current) return undefined;
    const prevIds = new Set(
      String(trackRecordScopeSigRef.current || '')
        .split(';')
        .flatMap((part) => {
          const idx = part.lastIndexOf(':');
          if (idx < 0) return [];
          const idsPart = part.slice(idx + 1);
          return idsPart ? idsPart.split(',').filter(Boolean) : [];
        })
    );
    trackRecordScopeSigRef.current = sig;

    (async () => {
      try {
        const onIds = Array.from(scopeIdSet);
        const onMembers = resolveMissionMembersByIds(onIds);
        const leftIds = Array.from(prevIds).filter((id) => id && !scopeIdSet.has(id));
        const leftMembers = resolveMissionMembersByIds(leftIds);
        if (leftMembers.length) {
          await syncTrackRecordForMembers(leftMembers, false);
        }
        if (cancelled) return;
        if (onMembers.length) {
          await syncTrackRecordForMembers(onMembers, true, activeMissionId);
          if (cancelled) return;
          for (const mem of onMembers) {
            if (cancelled) return;
            const name = mem?.name ? String(mem.name).trim() : '';
            if (!name) continue;
            const pts = await fetchRecentHistoryTrackPoints(mem, 180);
            if (cancelled) return;
            if (pts.length) seedRealtimeTrackPoints(name, pts);
          }
          if (cancelled) return;
          await ensureMissionRecordTrackDrawing(onMembers.map((m) => m?.name).filter(Boolean));
        }
      } catch (err) {
        console.warn('[mission] recordTracks sync/draw failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [
    settingsLoaded,
    missions,
    activeMissionId,
    resolveMissionMembersByIds,
    syncTrackRecordForMembers,
    seedRealtimeTrackPoints,
    ensureMissionRecordTrackDrawing
  ]);

  const isMissionMemberOnline = useCallback((member) => {
    if (!member) return false;
    const id = getCanonicalMemberId(member) || getMemberExpandKey(member);
    const status = (presenceById && id && presenceById[id])
      || getMemberPresenceStatus(member, Date.now(), memberTempOfflineTimeoutMs, memberOfflineTimeoutMs);
    return status === PRESENCE_STATUS.ONLINE || status === 'online';
  }, [presenceById, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs]);

  const getMissionMemberPresence = useCallback((member) => {
    if (!member) return PRESENCE_STATUS.OFFLINE;
    const id = getCanonicalMemberId(member) || getMemberExpandKey(member);
    return (presenceById && id && presenceById[id])
      || getMemberPresenceStatus(member, Date.now(), memberTempOfflineTimeoutMs, memberOfflineTimeoutMs)
      || PRESENCE_STATUS.OFFLINE;
  }, [presenceById, memberTempOfflineTimeoutMs, memberOfflineTimeoutMs]);

  const markVoiceUnread = useCallback((payload) => {
    const keys = voiceMemberKeys(payload);
    if (!keys.length) return;
    const entry = {
      keys,
      name: payload?.name || null,
      device_unique_id: payload?.device_unique_id || null,
      media: Array.isArray(payload?.media) ? payload.media : [],
      created_at: payload?.created_at || new Date().toISOString()
    };
    const map = voiceUnreadMapRef.current;
    keys.forEach((k) => map.set(k, entry));
    setVoicePulseIds((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
    const timers = voicePulseTimersRef.current;
    keys.forEach((k) => {
      const t = timers.get(k);
      if (t) { clearTimeout(t); timers.delete(k); }
    });
  }, []);

  const clearVoiceUnread = useCallback((payloadOrMember) => {
    const keys = voiceMemberKeys(payloadOrMember);
    if (!keys.length) return;
    const map = voiceUnreadMapRef.current;
    const extra = new Set(keys);
    keys.forEach((k) => {
      const entry = map.get(k);
      if (entry?.keys) entry.keys.forEach((x) => extra.add(x));
      map.delete(k);
    });
    extra.forEach((k) => map.delete(k));
    setVoicePulseIds((prev) => {
      const next = new Set(prev);
      extra.forEach((k) => next.delete(k));
      return next;
    });
  }, []);

  const handleVoicePttReceived = useCallback((payload) => {
    markVoiceUnread(payload);
  }, [markVoiceUnread]);
  handleVoicePttReceivedRef.current = handleVoicePttReceived;

  const handleVoicePttAutoPlayResult = useCallback((payload, result) => {
    if (result?.played) {
      clearVoiceUnread(payload);
      return;
    }
    markVoiceUnread(payload);
  }, [clearVoiceUnread, markVoiceUnread]);
  handleVoicePttAutoPlayResultRef.current = handleVoicePttAutoPlayResult;

  const tipUnreadVoicesForMemberIds = useCallback((memberIds) => {
    const summary = summarizeUnreadForMemberIds(
      voiceUnreadMapRef.current,
      memberIds,
      missionIdMatchesMember
    );
    if (!summary.count) return;
    if (summary.expiredCount > 0) {
      message.warning(`当前任务有 ${summary.count} 条语音未读（其中 ${summary.expiredCount} 条已过期，无法自动播放），请打开队员对话查看`);
    } else {
      message.info(`当前任务有 ${summary.count} 条语音未读，请点击队员旁语音图标收听（不会自动播放）`);
    }
  }, []);
  tipUnreadVoicesForMemberIdsRef.current = tipUnreadVoicesForMemberIds;

  useEffect(() => {
    const m = missionChatMember || memberChatTarget;
    if (m) clearVoiceUnread(m);
  }, [missionChatMember, memberChatTarget, clearVoiceUnread]);

  const handleMissionChatOutbound = useCallback((entry) => {
    if (!entry) return;
    try { appendMemberMessage?.(entry); } catch (_) { /* ignore */ }
  }, [appendMemberMessage]);

  flyToMemberRef.current = flyToMember;

  const handleOnlineNotificationClick = (notification) => {
    const list = membersRef.current || members;
    const member = (list || []).find((m) =>
      (m.device_unique_id || m.name) === notification?.memberId
      || m.name === notification?.memberName
    );
    if (!member) {
      message.warning(`${notification?.memberName || '该队员'} 暂无法定位`);
      return;
    }
    flyToMember(member);
  };

  // 在组件顶部引入useLayoutEffect
  useLayoutEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        document.querySelectorAll('.mapboxgl-ctrl-attrib, .mapboxgl-ctrl-logo').forEach(el => el.style.display = 'none');
      }, 500);
    }
  }, []); // 移除依赖项，只在组件挂载时执行一次

  const {
    mobileContainerHeight,
    isDragging,
    mobileContainerRef,
    handleMobileDragStart,
    handleExpandClick,
    handleCollapseClick
  } = useMobilePanelDrag({ isMobile });

  // 旧 HLS 初始化已迁移至 OptimizedVideoPlayer / LiveStreamModal



  // 须含 ortho*/mapLayer*；唯一来源 buildDashboardCommandFeatures，避免漏字段
  const commandFeatures = buildDashboardCommandFeatures();

  const memberTreeBlockProps = buildMemberTreeBlockProps({
    orgMap,
    openOrg,
    openGroup,
    openMember,
    setOpenOrg,
    setOpenGroup,
    setOpenMember,
    setHighlightedMemberIds,
    highlightedMemberName,
    setHighlightedMemberName,
    setActiveMemberId,
    showTracks,
    isMemberTrackVisible,
    streamingMembers,
    areAllGroupsExpanded,
    areAllMembersExpanded,
    expandOnlineInOrg,
    setOrgGroupsExpanded,
    expandOnlineInGroup,
    setGroupMembersExpanded,
    flyToMember,
    toggleTrack,
    openLiveStream,
    startRealTimeTrackDrawing,
    copyToClipboard,
    openDownloadTrackModal,
    fetchMemberTrack: handleFetchMemberTrack,
    realTimeTrackDrawing,
    stopTrackFeatures,
    hiddenMapMemberIds,
    onToggleMemberMapHidden: toggleMemberMapHidden,
    onlineTimeoutMs: memberTempOfflineTimeoutMs,
    offlineTimeoutMs: memberOfflineTimeoutMs,
    presenceById,
    commandFeatures,
    onSendLocationToMember: startSendLocation,
    onSendMessageToMember: openMemberChat,
    notesByKey,
    onSaveMemberNote: saveMemberNote,
    onMemberIconChange: (member, icon) => {
      const id = member?.device_unique_id || member?.name;
      if (!id) return;
      setMembers((prev) => prev.map((m) => (
        (m.device_unique_id || m.name) === id ? { ...m, icon } : m
      )));
    }
  });

  const layoutProps = buildDashboardLayoutProps({
    isMobile,
    mapContainer,
    mapRef,
    membersRef,
    members,
    drawingToolsActiveRef,
    mapLoadingState,
    isEarthAnimationActive,
    showLeftPanel,
    leftPanelVisible,
    mapStatus,
    rescueTargets,
    rescueTracks,
    mapStyles,
    currentMapStyleId,
    styleModeAvailability,
    onlineNotifications,
    activeMemberId,
    networkQuality,
    devicePerformance,
    refreshStatus,
    lastRefreshTime,
    sidebarRef,
    collapsed,
    sidebarWidth,
    sidebarMotionEnabled,
    startTime,
    endTime,
    showMemberNames,
    hideInvalid,
    showOnlineOnly,
    totalMemberCount: memberPanelTotalCount,
    onlineMemberCount: memberPanelOnlineCount,
    memberSearchQuery,
    onMemberSearchQueryChange: setMemberSearchQuery,
    filterMembers: listMembers,
    missionMode,
    missionMemberIds,
    setMissionMode,
    setMissionMemberIds,
    memberPanelTab,
    setMemberPanelTab,
    missionPickIds,
    onMissionPickIdsChange: setMissionPickIds,
    onJoinMission: handleJoinMission,
    missionGroups,
    onMissionGroupsChange: handleMissionGroupsChange,
    onRemoveMissionMember: handleRemoveMissionMember,
    onEndMission: handleEndMission,
    missions,
    activeMissionId,
    onSelectMission: handleSelectMission,
    onCreateMission: handleCreateMission,
    onRenameMission: handleRenameMission,
    onCloseMission: handleCloseMissionTab,
    onToggleMissionRecordTracks: handleToggleMissionRecordTracks,
    missionBadgeCount: countUniqueLiveAcrossMissions(missions, members),
    isMissionMemberOnline,
    getMissionMemberPresence,
    onFlyToMissionMember: flyToMember,
    voicePulseIds,
    streamingMembers,
    openLiveStream,
    notesByKey,
    onSaveMemberNote: saveMemberNote,
    onSendLocationToMember: startSendLocation,
    onSendMessageToMember: openMemberChat,
    getMap: () => mapRef.current,
    getMessagesForMember,
    onMissionChatOutbound: handleMissionChatOutbound,
    onMissionChatOpenChange: setMissionChatMember,
    onMissionChatMessageDeleted: removeMessageFromMap,
    historyEpoch,
    getBasemapId: () => currentBasemapIdRef.current,
    filteredCount: listMembers.length,
    refreshInterval,
    trackModeAndPlaybackUI: commandFeatures.playback ? trackModeAndPlaybackUI : null,
    memberTreeBlockProps,
    commandFeatures,
    mobileContainerRef,
    mobileContainerHeight,
    isDragging,
    allTracksVisible,
    showDownload,
    announcement,
    showAnnouncement,
    fieldMessageBanners,
    maxVisibleFieldBanners,
    onCloseFieldMessageBanner: dismissFieldMessageBanner,
    onFlyToFieldMessage: flyToFieldMessageMember,
    trackDownloadModal,
    showLiveStream,
    currentStreamMember,
    stopEarthAnimation,
    currentBasemapId,
    activeOverlayIds,
    loadRescueData,
    clearRescueData,
    toggleLeftPanel,
    switchMapStyle,
    showRescueTargetsList,
    showRescueTracksList,
    onRescueSearchSetTarget: plantRescueSearchTarget,
    renderBasemapLayerControl,
    locateCommander,
    fetchDevices,
    onSidebarExpand: (w) => {
      setCollapsed(false);
      saveSetting(SETTING_KEYS.COLLAPSED, false);
      dashboardDebug('侧边栏已展开，恢复宽度:', w + 'px');
    },
    handleDragMouseDown,
    handleResetDashboardConfig,
    handleReplayEarth,
    setShowMemberNames,
    handleShowOnlineTracks,
    handleDrawOnlineTracks,
    setHideInvalid,
    setShowOnlineOnly,
    onExportKml: () => { downloadAuthorizedTeamKml(); },
    setStartTime,
    setEndTime,
    handleRefreshIntervalChange,
    handleMobileDragStart,
    handleExpandClick,
    handleCollapseClick,
    toggleAllTracks,
    closeAnnouncement,
    setTrackDownloadModal,
    confirmDownloadMemberTrack,
    closeLiveStream,
    onOnlineNotificationClick: handleOnlineNotificationClick
  });

  return (
    <>
      <DashboardAuthedWorkspace
        layoutProps={layoutProps}
        trackLineActionProps={trackLineActionProps}
        commandFeatures={commandFeatures}
        memberChatTarget={memberChatTarget}
        setMemberChatTarget={setMemberChatTarget}
        getMessagesForMember={getMessagesForMember}
        appendMemberMessage={appendMemberMessage}
        placeOutboundCommandPin={placeOutboundCommandPin}
        removeMessageFromMap={removeMessageFromMap}
        historyEpoch={historyEpoch}
        getMap={() => mapRef.current}
        getBasemapId={() => currentBasemapIdRef.current}
        openLiveStream={openLiveStream}
        streamingMembers={streamingMembers}
        notesByKey={notesByKey}
        plantTarget={plantTarget}
        planted={planted}
        isMobile={isMobile}
        sendingLocation={sendingLocation}
        cancelSendLocation={cancelSendLocation}
        confirmSendLocation={confirmSendLocation}
        composeOpen={composeOpen}
        closeSendLocationCompose={closeSendLocationCompose}
        submitSendLocationCompose={submitSendLocationCompose}
        members={members}
        onlineTimeoutMs={memberTempOfflineTimeoutMs}
        rescueSearchTargetPushModalProps={rescueSearchTargetPushModalProps}
      />
      <PickPointSubmittedModal
        visible={pickPointSubmittedModal.visible}
        point={pickPointSubmittedModal.point}
        onFlyTo={flyToPickPoint}
        onConfirm={confirmPickPoint}
        onClose={() => setPickPointSubmittedModal({ visible: false, point: null, confirming: false })}
        confirming={pickPointSubmittedModal.confirming}
      />
    </>
  );
}

export default DashboardAuthenticated;
